
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { 
  Trash2,
  AlertTriangle,
  Users, 
  UserPlus, 
  LayoutDashboard, 
  Settings as SettingsIcon, 
  AlertCircle, 
  TrendingUp, 
  ChevronRight,
  Clipboard,
  Download,
  LogOut,
  BrainCircuit,
  Building,
  CheckCircle,
  Phone,
  Mail,
  Globe,
  MapPin,
  Image as ImageIcon,
  MessageSquareText,
  Moon,
  Sun,
  Palette,
  Mic,
  Sparkles,
  Camera,
  History,
  User as UserIcon,
  Calendar,
  Wallet,
  Video,
  CreditCard,
  FileText,
  FolderOpen,
  HelpCircle,
  PlayCircle,
  Menu,
  ArrowLeft,
  Pencil,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Plus,
  Search,
  X,
  MessageCircle,
  CalendarClock,
} from 'lucide-react';
import { motion } from 'motion/react';
import { Patient, Therapist, ClinicalCycle, Approach, CycleFrequency, AudioAnalysis, SubscriptionInfo, SubscriptionPlan, SubscriptionStatus, ClinicalRecord, Medication } from './types';
import { INDICATORS } from './constants';
import jsPDF from 'jspdf';
import PatientList from './components/PatientList';
import PatientDetails from './components/PatientDetails';
import NewPatientModal from './components/NewPatientModal';
import ClinicalAssistant from './components/ClinicalAssistant';
import Recordings from './components/Recordings';
import AIAnalysis from './components/AIAnalysis';
import Transcriptions from './components/Transcriptions';
import Agenda from './components/Agenda';
import { AgendaSettings, SERVICES_STORAGE_KEY } from './components/AgendaSettings';
import { purgeLegacyAgendaKeys, scopedKey, setStorageScope } from './utils/scopedStorage';
import Finance from './components/Finance';
import Teleconsultation from './components/Teleconsultation';
import Repository from './components/Repository';
import TermsOfUse from './components/TermsOfUse';
import { AllodosLogo } from './components/AllodosLogo';
import LGPDChecklist from './components/LGPDChecklist';
import ResetPassword from './components/ResetPassword';
import Waitlist from './components/Waitlist';
import BackupSettings from './components/BackupSettings';
import ImportData from './components/ImportData';
import OnboardingWizard from './components/OnboardingWizard';
import Login from './components/Login';
import SubscriptionManagement from './components/SubscriptionManagement';
import { subscriptionService } from './services/subscriptionService';
import { getAudioBlob } from './utils/audioDb';
import { patientsVisibleToProfile, selectablePatients } from './utils/patientAccess';
import { describeError } from './utils/describeError';
import { medicationList } from './utils/medications';
import { canEditClinicIdentity, canEditContactWhatsapp, canManageBookingPage, permissionsForRole } from './utils/rolePermissions';
import { LGPD_CHECKLIST_DOCUMENT, TERMS_DOCUMENT } from './content/legalDocuments';
import { clearResetTokenFromUrl, readResetTokenFromUrl } from './services/authApi';
import { clearInviteTokenFromUrl, createInvitation, fetchInvitationLink, fetchTeamMembers, readInviteTokenFromUrl, removeTeamMember, resolveInvitation, revokeInvitation, toRoleLabel, type ResolvedInvitation, type TeamMember } from './services/teamApi';
import { readBookingSlugFromUrl } from './services/bookingApi';
import PatientPortal from './components/PatientPortal';
import AppointmentAction from './components/AppointmentAction';
import { readAppointmentActionFromUrl } from './services/appointmentActionsApi';
import { clearToken, hasToken, UNAUTHORIZED_EVENT_NAME } from './services/apiClient';
import { me, type AuthUser } from './services/authApi';
import { acknowledgeLegalDocument, updateUserProfile } from './services/usersApi';
import { fetchDashboardSummary, type DashboardSummary } from './services/dashboardApi';
import { fetchSubscriptionStatus, mapBillingToSubscriptionInfo } from './services/subscriptionsApi';
import { updateReportTexts } from './services/reportsApi';
import { createClinicalRecord, createPatient, deletePatient, fetchPatients, setPatientsStatus, type CreatePatientPayload, updatePatient } from './services/patientsApi';
import { createCycle, deleteCycle, updateCycle } from './services/cyclesApi';
import { interpretCycle, registerAiConsent } from './services/aiApi';
import { deleteRecording as deleteRemoteRecording, deleteRecordingTranscription, fetchRecordings } from './services/recordingsApi';

// Bloco #77: as chaves globais antigas do localStorage misturavam contas no
// mesmo navegador. São descartadas uma vez; o que vale está no servidor.
purgeLegacyAgendaKeys();

// Fix: Added sub-components that were missing but used in the layout
const SidebarItem: React.FC<{ icon: React.ReactNode, label: string, active: boolean, onClick: () => void, rightIcon?: React.ReactNode }> = ({ icon, label, active, onClick, rightIcon }) => (
  <li>
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
        active 
          ? 'bg-white/15 text-white shadow-lg shadow-black/20' 
          : 'text-indigo-300/70 hover:bg-white/5 hover:text-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`${active ? 'text-white' : 'text-indigo-400'}`}>{icon}</span>
        {label}
      </div>
      {rightIcon && <span className="flex items-center shrink-0">{rightIcon}</span>}
    </button>
  </li>
);

const StatCard: React.FC<{ title: string, value: string, icon: React.ReactNode, onClick?: () => void }> = ({ title, value, icon, onClick }) => (
  <div 
    onClick={onClick}
    className={`bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm transition-all ${onClick ? 'cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-800 hover:shadow-md' : 'hover:shadow-md'}`}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl">{icon}</div>
    </div>
    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">{title}</p>
    <p className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter">{value}</p>
  </div>
);

/**
 * Copia texto para a área de transferência. `navigator.clipboard` só existe em
 * HTTPS (ou localhost); fora disso usa o método antigo, para o botão nunca falhar.
 */
async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error('O navegador bloqueou a cópia. Selecione e copie o link manualmente.');
}

const profileFromTeamMember = (member: TeamMember) => {
  const role = member.roleLabel || toRoleLabel(member.role);
  return {
    id: member.id,
    tenantId: member.tenantId,
    name: member.name,
    role,
    email: member.email,
    status: member.status,
    crp: member.crp || '',
    avatarUrl: member.avatarUrl || '',
    professionalPhoto: member.professionalPhoto || '',
    clinicName: member.clinicName || '',
    invitationId: member.invitationId,
    permissions: permissionsForRole(role),
  };
};

const PatientRow: React.FC<{ patient: Patient, onClick: () => void, onEditClick: () => void, getInitials: (name: string) => string }> = ({ patient, onClick, onEditClick, getInitials }) => (
  <div 
    onClick={onClick}
    className="flex items-center justify-between p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 hover:border-indigo-200 cursor-pointer transition-all shadow-sm group"
  >
    <div className="flex items-center gap-4">
      <div className="relative">
        <div className="w-12 h-12 bg-indigo-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center font-black text-indigo-600 dark:text-indigo-400 text-sm shadow-inner group-hover:scale-105 transition-transform">
          {getInitials(patient.name)}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditClick();
          }}
          className="absolute -bottom-1 -right-1 p-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-md hover:scale-110 active:scale-95 transition-all z-10 border border-white dark:border-slate-900 cursor-pointer animate-in fade-in zoom-in-50 duration-300"
          title="Editar Paciente"
        >
          <Pencil size={10} />
        </button>
      </div>
      <div>
        <h4 className="font-black text-slate-800 dark:text-slate-100 text-sm tracking-tight">{patient.name}</h4>
        <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
          {patient.activeApproach?.split('(')[0]} • {patient.cycles?.length ?? 0} ciclos
        </p>
      </div>
    </div>
    <ChevronRight className="text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" size={20} />
  </div>
);

/** Média dos scores de um ciclo, tolerante a ciclo sem indicadores. */
const avgScore = (scores?: { score: number }[]): number => {
  if (!scores?.length) return 0;
  return scores.reduce((acc, s) => acc + s.score, 0) / scores.length;
};

const defaultTherapist: Therapist = {
  name: '',
  crp: '',
  clinicName: '',
  email: '',
  phone: '',
  website: '',
  address: '',
  logo: '',
};

const resizeImageToDataUrl = (file: File, maxSize = 256, quality = 0.72): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Imagem inválida.'));
        return;
      }
      image.src = reader.result;
    };

    image.onerror = () => reject(new Error('Não foi possível carregar a imagem.'));
    image.onload = () => {
      const ratio = Math.min(1, maxSize / Math.max(image.width, image.height));
      const width = Math.max(1, Math.round(image.width * ratio));
      const height = Math.max(1, Math.round(image.height * ratio));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('Não foi possível processar a imagem.'));
        return;
      }
      context.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };

    reader.readAsDataURL(file);
  });

const App: React.FC = () => {
  const isUuid = (value?: string): boolean => !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const normalizeMedications = useCallback((medications: Patient['medications'] | string | null | undefined): string | undefined => {
    if (Array.isArray(medications)) {
      const compact = medications
        .map((m) => [m.name, m.quantity, m.time].filter(Boolean).join(' '))
        .map((s) => s.trim())
        .filter(Boolean);
      return compact.length ? compact.join(' | ') : undefined;
    }
    if (typeof medications === 'string') {
      const trimmed = medications.trim();
      return trimmed || undefined;
    }
    return undefined;
  }, []);

  const therapistFromUser = useCallback((user: AuthUser): Therapist => {
    // Para quem não é administrador, a identidade da clínica vem do
    // administrador do tenant (`clinic` no /auth/me) e é somente leitura.
    const clinic = user.teamRole && user.teamRole !== 'ADMINISTRADOR' && user.clinic ? user.clinic : null;
    return {
      name: user.name,
      crp: user.crp || '',
      clinicName: (clinic ? clinic.clinicName : user.clinicName) || '',
      email: (clinic ? clinic.email : user.email) || '',
      phone: (clinic ? clinic.phone : user.phone) || '',
      website: (clinic ? clinic.website : user.website) || '',
      address: (clinic ? clinic.address : user.address) || '',
      cnpj: (clinic ? clinic.cnpj : user.cnpj) || '',
      logo: (clinic ? clinic.avatarUrl : user.avatarUrl) || '',
      professionalPhoto: user.professionalPhoto || '',
      whatsapp: user.whatsapp || '',
      // Recepcionista tem o próprio WhatsApp em "Contato e Localização" (bloco #40);
      // profissional vê o da clínica, só leitura.
      clinicWhatsapp: (user.teamRole === 'RECEPCIONISTA' || !clinic ? user.clinicWhatsapp : clinic.clinicWhatsapp) || '',
    };
  }, []);

  const [isAuthenticated, setIsAuthenticated] = useState(() => hasToken() || localStorage.getItem('allodos-auth') === 'true');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth < 1024);
  // Celular/tablet: o menu é um drawer sobre o conteúdo; fecha ao escolher
  // uma tela (senão fica aberto cobrindo a página escolhida).
  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 1024) setIsSidebarCollapsed(true);
  };
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => {
    const saved = localStorage.getItem('allodos-user');
    return saved ? (JSON.parse(saved) as AuthUser) : null;
  });
  // Bloco #77: o cache local da agenda (serviços, locais, horários, página de
  // agendamento) é isolado pela clínica autenticada. Definido aqui, antes de
  // qualquer filho renderizar, para as telas já lerem a chave certa.
  setStorageScope(currentUser?.tenantId || currentUser?.id || '');

  // Termos e Checklist LGPD no primeiro acesso (blocos 30/31): a decisão é por
  // usuário e vem do servidor (versão lida x versão vigente do documento), e
  // não mais de um flag do navegador — um usuário novo no mesmo computador
  // precisa ver os documentos. Enquanto o perfil não carrega, nada é exibido.
  const [legalGateBusy, setLegalGateBusy] = useState(false);
  const hasAcceptedTerms =
    !!currentUser && currentUser.legalTermsReadVersion === TERMS_DOCUMENT.version;
  const hasAcceptedLGPD =
    !!currentUser && currentUser.lgpdChecklistReadVersion === LGPD_CHECKLIST_DOCUMENT.version;
  const legalStatusKnown = !!currentUser && currentUser.legalTermsReadVersion !== undefined;
  const [showWizard, setShowWizard] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(() => subscriptionService.getSubscription());

  // Bloco #56: a volta do Google (`/?google=...`) abre direto na Teleconsulta.
  const [activeTab, setActiveTab] = useState<'dashboard' | 'patients' | 'cycles' | 'settings' | 'user-profiles' | 'assistant' | 'recordings' | 'ai-analysis' | 'transcriptions' | 'agenda' | 'agenda-settings' | 'finance' | 'booking-site' | 'teleconsultation' | 'subscriptions' | 'repository' | 'waitlist'>(
    () => (new URLSearchParams(window.location.search).has('google') ? 'teleconsultation' : 'dashboard'),
  );
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [filterAlertsOnly, setFilterAlertsOnly] = useState(false);
  const [autoOpenEditProfile, setAutoOpenEditProfile] = useState(false);
  const [initialSubTab, setInitialSubTab] = useState<'evolution' | 'prontuario' | 'files' | undefined>(undefined);
  const [autoOpenProntuarioDate, setAutoOpenProntuarioDate] = useState<string | undefined>(undefined);
  const [isAgendaMenuOpen, setIsAgendaMenuOpen] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [pendingRecordingId, setPendingRecordingId] = useState<string | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isSavingPatient, setIsSavingPatient] = useState(false);
  const [newPatientError, setNewPatientError] = useState<string | null>(null);
  const [cycleError, setCycleError] = useState<string | null>(null);
  const [isGeneratingIA, setIsGeneratingIA] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showSettingsToast, setShowSettingsToast] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
   const [userProfiles, setUserProfiles] = useState<any[]>(() => {
    try {
      const saved = localStorage.getItem('allodos-user-profiles');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Normalize role and set default permissions based on current requirements
        return parsed.map((p: any) => {
          // Normalize role strictly to 'Administrador' | 'Profissional' | 'Recepcionista'
          if (p.role === 'Secretária' || p.role.toLowerCase().includes('secretar') || p.role.toLowerCase().includes('recep')) {
            p.role = 'Recepcionista';
          } else if (p.role === 'Administrador' || p.role.includes('Admin')) {
            p.role = 'Administrador';
          } else {
            p.role = 'Profissional';
          }

          // Permissões sempre pela tabela oficial por papel.
          p.permissions = permissionsForRole(p.role);
          return p;
        });
      }
    } catch (e) {}
    return [
      { 
        id: '1', 
        name: 'Dra. Marina Lopes', 
        role: 'Administrador', 
        email: 'contato@clinicaevoluir.com', 
        status: 'Ativo',
        permissions: { agenda: true, patients: true, finance: true, teleconsultation: true, repository: true, settings: true, supervisorChat: true, subscriptions: true }
      },
      { 
        id: '2', 
        name: 'Ana Paula Silva', 
        role: 'Recepcionista', 
        email: 'recepcao@clinicaevoluir.com', 
        status: 'Ativo',
        permissions: { agenda: true, patients: true, finance: true, teleconsultation: true, repository: true, settings: false, supervisorChat: true, subscriptions: true }
      }
    ];
  });

  useEffect(() => {
    localStorage.setItem('allodos-user-profiles', JSON.stringify(userProfiles));
  }, [userProfiles]);

  const [activeProfileId, setActiveProfileId] = useState<string>(() => {
    return localStorage.getItem('allodos-active-profile-id') || '1';
  });

  const activeProfile = useMemo(() => {
    const profile = userProfiles.find(p => p.id === activeProfileId);
    if (profile) return profile;
    return userProfiles[0] || { 
      id: '1', 
      name: 'Dra. Marina Lopes', 
      role: 'Administrador', 
      email: 'contato@clinicaevoluir.com', 
      status: 'Ativo',
      permissions: { agenda: true, patients: true, finance: true, teleconsultation: true, repository: true, settings: true, supervisorChat: true, subscriptions: true }
    };
  }, [userProfiles, activeProfileId]);

  useEffect(() => {
    localStorage.setItem('allodos-active-profile-id', activeProfileId);
  }, [activeProfileId]);

  // Sincronizar activeProfileId de acordo com o e-mail do usuário logado
  useEffect(() => {
    if (currentUser && userProfiles.length > 0) {
      const profile = userProfiles.find(p => p.email?.toLowerCase() === currentUser.email?.toLowerCase());
      if (profile) {
        if (profile.id !== activeProfileId) {
          setActiveProfileId(profile.id);
          localStorage.setItem('allodos-active-profile-id', profile.id);
          window.dispatchEvent(new Event('profile-switched'));
        }
      }
    }
  }, [currentUser, userProfiles, activeProfileId]);

  const [invitedProfile, setInvitedProfile] = useState<any | null>(null);
  const [loginPreFill, setLoginPreFill] = useState<{ email: string; name: string; isRegister: boolean } | null>(() =>
    readInviteTokenFromUrl() ? { email: '', name: '', isRegister: true } : null,
  );
  // Convite aberto pelo link do e-mail (`?invite=`).
  const [inviteToken, setInviteToken] = useState<string | null>(() => readInviteTokenFromUrl());
  const [resetToken, setResetToken] = useState<string | null>(() => readResetTokenFromUrl());
  const [openForgot, setOpenForgot] = useState(false);
  const [bookingSlug] = useState<string | null>(() => readBookingSlugFromUrl());
  const [appointmentAction] = useState(() => readAppointmentActionFromUrl());
  const [invitation, setInvitation] = useState<ResolvedInvitation | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyLink = (link: string) => {
    void copyText(link);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Blocos #58/#59: retorno visível das ações na lista de perfis.
  const [teamNotice, setTeamNotice] = useState<{ ok: boolean; msg: string } | null>(null);
  const showTeamNotice = (ok: boolean, msg: string) => {
    setTeamNotice({ ok, msg });
    window.setTimeout(() => setTeamNotice(null), 6000);
  };

  /**
   * Bloco #59: "Copiar Convite" busca na API o link daquele convite — o mesmo
   * do e-mail e do modal de envio — em vez de depender do que ficou na tela.
   */
  const handleCopyInvite = async (profile: any) => {
    let url: string | undefined;
    try {
      const link = profile.invitationId ? await fetchInvitationLink(profile.invitationId) : null;
      url = link?.inviteUrl || profile.inviteUrl;
      if (!url) throw new Error('Convite não encontrado. Reenvie o convite deste perfil.');
      await copyText(url);
      showTeamNotice(
        true,
        link?.renewed
          ? `Convite de ${profile.name} copiado. O convite antigo tinha vencido ou era de uma versão anterior: este é um link novo, e o anterior não vale mais.`
          : `Convite de ${profile.name} (${profile.email}) copiado.`,
      );
    } catch (err) {
      // Com o link em mãos, mostra-o para copiar à mão (o navegador pode bloquear a cópia).
      showTeamNotice(false, url ? `Não foi possível copiar automaticamente. Convite de ${profile.name}: ${url}` : `Não foi possível copiar o convite: ${describeError(err)}`);
    }
  };

  // Bloco #58: exclusão só com confirmação explícita no pop-up.
  const [profileToDelete, setProfileToDelete] = useState<any | null>(null);
  const [isDeletingProfile, setIsDeletingProfile] = useState(false);
  const confirmDeleteProfile = async () => {
    const profile = profileToDelete;
    if (!profile) return;
    setIsDeletingProfile(true);
    try {
      if (profile.invitationId) await revokeInvitation(profile.invitationId);
      else await removeTeamMember(profile.id);
      setUserProfiles((prev) => prev.filter((p) => p.id !== profile.id));
      showTeamNotice(true, `Perfil de ${profile.name} excluído.`);
      setProfileToDelete(null);
      refetchTeamMembersRef.current?.();
    } catch (err) {
      showTeamNotice(false, `Não foi possível excluir o perfil de ${profile.name}: ${describeError(err)}`);
    } finally {
      setIsDeletingProfile(false);
    }
  };
  const refetchTeamMembersRef = useRef<(() => void) | null>(null);

  const handleSimulateRegistration = (profile: any) => {
    handleLogout();
    // Precisa carregar o token do convite: sem ele o cadastro cai no papel
    // padrão (Administrador), justamente o bug que o convite resolve.
    const token = profile?.inviteUrl ? readInviteTokenFromUrl(new URL(profile.inviteUrl).search) : null;
    if (token) setInviteToken(token);
    setLoginPreFill({
      email: profile.email,
      name: profile.name,
      isRegister: true
    });
    setInvitedProfile(null);
  };

  const switchProfile = (id: string) => {
    setActiveProfileId(id);
    localStorage.setItem('allodos-active-profile-id', id);
    window.dispatchEvent(new Event('profile-switched'));
  };

  const [isProfileSwitcherOpen, setIsProfileSwitcherOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<any | null>(null);
  const [profileForm, setProfileForm] = useState({
    name: '',
    role: 'Profissional',
    email: '',
    status: 'Ativo',
    permissions: {
      agenda: true,
      patients: true,
      finance: false,
      teleconsultation: true,
      repository: true,
      subscriptions: false,
      supervisorChat: true,
      settings: false
    }
  });

  const handleRoleChangeInForm = (role: string) => {
    setProfileForm(prev => ({
      ...prev,
      role,
      permissions: permissionsForRole(role),
    }));
  };

  const openAddProfileModal = () => {
    setEditingProfile(null);
    setProfileForm({
      name: '',
      role: 'Profissional',
      email: '',
      status: 'Aguardando Cadastro',
      permissions: {
        agenda: true,
        patients: true,
        finance: false,
        teleconsultation: true,
        repository: true,
        subscriptions: false,
        supervisorChat: true,
        settings: false
      }
    });
    setIsProfileModalOpen(true);
  };

  const openEditProfileModal = (profile: any) => {
    setEditingProfile(profile);
    const resolvedRole = (profile.role === 'Secretária' || profile.role === 'Secretaria') ? 'Recepcionista' : (profile.role || 'Profissional');
    setProfileForm({
      name: profile.name,
      role: resolvedRole,
      email: profile.email,
      status: profile.status || 'Ativo',
      permissions: profile.permissions || {
        agenda: true,
        patients: true,
        finance: resolvedRole === 'Administrador',
        teleconsultation: resolvedRole !== 'Recepcionista',
        repository: resolvedRole !== 'Recepcionista',
        subscriptions: resolvedRole === 'Administrador',
        supervisorChat: resolvedRole !== 'Recepcionista',
        settings: resolvedRole === 'Administrador'
      }
    });
    setIsProfileModalOpen(true);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileForm.name || !profileForm.email) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (editingProfile) {
      setUserProfiles(prev => prev.map(p => p.id === editingProfile.id ? { ...p, ...profileForm } : p));
      setIsProfileModalOpen(false);
      setEditingProfile(null);
      return;
    }

    // Novo membro: o convite é criado no backend, que dispara o e-mail com o
    // link de criação de senha já carregando o papel escolhido.
    setIsInviting(true);
    try {
      const invitation = await createInvitation({
        email: profileForm.email,
        name: profileForm.name,
        role: profileForm.role,
      });

      const newProfile = {
        id: Math.random().toString(36).substring(2, 9),
        ...profileForm,
        status: 'Aguardando Cadastro',
        invitationId: invitation.id,
        inviteUrl: invitation.inviteUrl,
        emailDelivery: invitation.emailDelivery,
      };
      setUserProfiles(prev => [...prev, newProfile]);
      setInvitedProfile(newProfile);
      setIsProfileModalOpen(false);
      setEditingProfile(null);
      fetchTeamMembers()
        .then((members) => {
          const profiles = members.map(profileFromTeamMember);
          if (profiles.length) setUserProfiles(profiles);
        })
        .catch(() => undefined);
    } catch (err) {
      console.error(err);
      alert(`Não foi possível enviar o convite.\n\n${describeError(err)}`);
    } finally {
      setIsInviting(false);
    }
  };
  
  const [recordingsList, setRecordingsList] = useState<any[]>(() => []);

  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('allodos-theme') === 'dark');
  const [exportPatientId, setExportPatientId] = useState<string>('');
  
  const [patients, setPatients] = useState<Patient[]>(() => []);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState<string | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);

  const filteredPatients = useMemo(
    () => patientsVisibleToProfile(patients, activeProfile),
    [patients, activeProfile],
  );

  /**
   * Pacientes arquivados (inativos) não aparecem no painel nem podem ser
   * escolhidos em agendamentos, gravações ou análises.
   */
  const activePatients = useMemo(() => selectablePatients(filteredPatients), [filteredPatients]);

  const [dashboardSearchTerm, setDashboardSearchTerm] = useState('');

  /** Profissionais da clínica (administradores e profissionais) para o filtro da lista. */
  const clinicProfessionals = useMemo(
    () =>
      userProfiles
        // Convites pendentes (id "inv:…") ainda não são usuários da API.
        .filter((p: any) => (p.role === 'Administrador' || p.role === 'Profissional') && !String(p.id || '').startsWith('inv:'))
        .map((p: any) => ({ id: String(p.id), name: String(p.name || p.email || '') })),
    [userProfiles],
  );

  /** Só o administrador edita a identidade da clínica e vê a Zona de Perigo. */
  const canEditClinic = canEditClinicIdentity(activeProfile?.role);
  const canEditContactWhatsappField = canEditContactWhatsapp(activeProfile?.role);

  const [therapist, setTherapist] = useState<Therapist>(() => defaultTherapist);

  /** Serviços cadastrados na Agenda viram categorias de entrada no livro caixa. */
  const serviceNames = useMemo(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(scopedKey(SERVICES_STORAGE_KEY)) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((s: any) => s?.active !== false)
        .map((s: any) => String(s?.name || ''))
        .filter(Boolean);
    } catch {
      return [];
    }
  }, [activeTab]);

  /** Identidade exibida no rodapé da barra lateral (foto pessoal > logo > iniciais). */
  const sidebarAvatar =
    therapist.professionalPhoto || therapist.logo || currentUser?.professionalPhoto || currentUser?.avatarUrl || '';
  const sidebarName = therapist.name || activeProfile?.name || currentUser?.name || '';

  useEffect(() => {
    localStorage.setItem('allodos-theme', isDarkMode ? 'dark' : 'light');
    const root = window.document.documentElement;
    if (isDarkMode) root.classList.add('dark');
    else root.classList.remove('dark');
  }, [isDarkMode]);

  const refetchPatients = useCallback(async () => {
    if (!isAuthenticated) return;
    setPatientsLoading(true);
    setPatientsError(null);
    try {
      const data = await fetchPatients();
      setPatients(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error(err);
      setPatientsError(err?.message || 'Não foi possível carregar os pacientes.');
    } finally {
      setPatientsLoading(false);
    }
  }, [isAuthenticated]);

  const refetchRecordings = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await fetchRecordings();
      setRecordingsList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  }, [isAuthenticated]);

  const refetchTeamMembers = useCallback(async () => {
    if (!isAuthenticated || !hasToken()) return;
    try {
      const members = await fetchTeamMembers();
      const profiles = members.map(profileFromTeamMember);
      if (profiles.length) {
        setUserProfiles(profiles);
        localStorage.setItem('allodos-user-profiles', JSON.stringify(profiles));
      }
    } catch (err) {
      console.error(err);
    }
  }, [isAuthenticated]);

  refetchTeamMembersRef.current = refetchTeamMembers;

  useEffect(() => {
    if (!isAuthenticated) return;
    refetchPatients();
    refetchRecordings();
    refetchTeamMembers();
  }, [isAuthenticated, refetchPatients, refetchRecordings, refetchTeamMembers]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    me()
      .then((user) => {
        if (cancelled) return;
        setCurrentUser(user);
        setTherapist(therapistFromUser(user));
        localStorage.setItem('allodos-user', JSON.stringify(user));
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, therapistFromUser]);

  useEffect(() => {
    if (!isAuthenticated || !hasToken()) return;
    (async () => {
      try {
        const billing = await fetchSubscriptionStatus();
        const mapped = mapBillingToSubscriptionInfo(billing);
        setSubscription(mapped);
        subscriptionService.saveSubscription(mapped);
        const summary = await fetchDashboardSummary();
        setDashboardSummary(summary);
      } catch {
        /* offline ou sem permissão — os guards da API tratam */
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    const onUnauthorized = () => handleLogout();
    window.addEventListener(UNAUTHORIZED_EVENT_NAME, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT_NAME, onUnauthorized);
  }, []);

  // Link do convite: resolve o token, força a tela de cadastro e pré-preenche
  // nome/e-mail. Antes o link caía no painel em vez da criação de senha.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;

    // Uma sessão antiga não pode "engolir" o convite de outra pessoa.
    if (hasToken() || localStorage.getItem('allodos-auth') === 'true') {
      handleLogout();
    }

    resolveInvitation(inviteToken)
      .then((data) => {
        if (cancelled) return;
        setInvitation(data);
        setInviteError(null);
        setLoginPreFill({ email: data.email, name: data.name, isRegister: true });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setInviteError(describeError(err));
        setInviteToken(null);
        clearInviteTokenFromUrl();
      });

    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  // Recupera URLs de áudio salvas localmente (IndexedDB) para gravações locais
  useEffect(() => {
    const reviveAudioUrls = async () => {
      try {
        setRecordingsList((prev) => prev);
        const current = recordingsList;
        if (!Array.isArray(current) || current.length === 0) return;

        const updatedList = await Promise.all(
          current.map(async (rec) => {
            if (rec.type === 'audio' && rec.id && !rec.url) {
              const blob = await getAudioBlob(rec.id);
              if (blob) {
                return {
                  ...rec,
                  url: URL.createObjectURL(blob)
                };
              }
            }
            return rec;
          })
        );

        setRecordingsList(updatedList);
      } catch (err) {
        console.error('Error reviving audio URLs from IndexedDB', err);
      }
    };
    reviveAudioUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientsLoading]);

  // Efeito para verificar status da assinatura e realizar deleção se necessário
  useEffect(() => {
    const checkSub = () => {
      const current = subscriptionService.getSubscription();
      setSubscription(current);

      if (subscriptionService.shouldDeleteData(current)) {
        handleDeleteAllData();
        // Reset sub to a new trial if needed or keep as expired
      }
    };

    checkSub();
    const interval = setInterval(checkSub, 1000 * 60 * 60); // Check every hour
    return () => clearInterval(interval);
  }, []);

  // Listen to navigation event from Agenda to Patient's Prontuário
  useEffect(() => {
    const handleNavigate = (e: Event) => {
      const customEvent = e as CustomEvent<{ patientId: string; date?: string }>;
      const { patientId, date } = customEvent.detail;
      setSelectedPatientId(patientId);
      setInitialSubTab('prontuario');
      if (date) {
        setAutoOpenProntuarioDate(date);
      }
      setActiveTab('cycles');
    };

    window.addEventListener('navigate-to-patient-prontuario', handleNavigate);
    return () => {
      window.removeEventListener('navigate-to-patient-prontuario', handleNavigate);
    };
  }, []);

  const dashboardStats = useMemo(() => {
    // Sem resumo da API, calcula sobre a carteira visível ao perfil (o
    // profissional só vê os próprios pacientes), nunca sobre a clínica toda.
    const patients = filteredPatients;
    const totalPatients = patients.length;
    const totalCycles = patients.reduce((acc, p) => acc + (p.cycles?.length || 0), 0);
    
    const activeAlerts = patients.filter(p => {
      if (p.cycles && p.cycles.length > 0) {
        const lastCycle = p.cycles[p.cycles.length - 1];
        return lastCycle.subjectiveDistress >= 8 || lastCycle.functionalImpairment >= 8;
      }
      return false;
    }).length;

    let totalEvolution = 0;
    let patientsWithProgress = 0;

    patients.forEach(p => {
      if (p.cycles && p.cycles.length >= 2) {
        const firstCycle = p.cycles[0];
        const lastCycle = p.cycles[p.cycles.length - 1];
        
        const firstAvg = avgScore(firstCycle.scores);
        const lastAvg = avgScore(lastCycle.scores);
        
        const diff = lastAvg - firstAvg;
        totalEvolution += diff;
        patientsWithProgress++;
      }
    });

    const avgEvolution = patientsWithProgress > 0 ? (totalEvolution / patientsWithProgress).toFixed(1) : "0.0";
    const evolutionPrefix = Number(avgEvolution) > 0 ? "+" : "";

    if (dashboardSummary) {
      const rate = dashboardSummary.evolutionRate;
      return {
        totalPatients: dashboardSummary.totalPatients,
        totalCycles: dashboardSummary.totalCycles,
        activeAlerts: dashboardSummary.alerts?.length ?? 0,
        evolutionRate: rate == null ? 'N/D' : `${rate > 0 ? '+' : ''}${rate}%`,
        alerts: dashboardSummary.alerts ?? [],
      };
    }

    return {
      totalPatients,
      totalCycles,
      activeAlerts,
      evolutionRate: `${evolutionPrefix}${avgEvolution}`,
      alerts: [] as DashboardSummary['alerts'],
    };
  }, [dashboardSummary, filteredPatients]);

  const handleLogin = (user: AuthUser) => {
    const syncedSubscription = subscriptionService.syncFromBackend(user);
    setIsAuthenticated(true);
    setSubscription(syncedSubscription);
    setPatients([]);
    setRecordingsList([]);

    // Look up if this email matches an existing profile
    const existingProfile = userProfiles.find(p => p.email?.toLowerCase() === user.email?.toLowerCase());

    const loggedInUser: AuthUser = {
      ...user,
      name: existingProfile ? existingProfile.name : user.name,
    };

    setCurrentUser(loggedInUser);

    // O papel é o que o backend devolve (definido no convite do administrador).
    // Antes, qualquer cadastro novo virava "Administrador" — inclusive um
    // convite emitido como Recepcionista, porque os perfis só existiam no
    // localStorage do administrador e nunca chegavam ao navegador do convidado.
    const role = toRoleLabel(user.teamRole);
    const permissions = permissionsForRole(role);

    setUserProfiles(prev => {
      const idx = prev.findIndex(p => p.email?.toLowerCase() === user.email?.toLowerCase());
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = {
          ...updated[idx],
          role,
          permissions,
          status: updated[idx].status === 'Aguardando Cadastro' ? 'Ativo' : updated[idx].status,
          name: user.name || updated[idx].name,
        };
        setActiveProfileId(updated[idx].id);
        localStorage.setItem('allodos-active-profile-id', updated[idx].id);
        return updated;
      }

      const created = {
        id: Math.random().toString(36).substring(2, 9),
        name: loggedInUser.name,
        email: loggedInUser.email,
        role,
        status: 'Ativo',
        permissions,
      };
      setActiveProfileId(created.id);
      localStorage.setItem('allodos-active-profile-id', created.id);
      return [...prev, created];
    });

    setTherapist(therapistFromUser(loggedInUser));
    localStorage.setItem('allodos-auth', 'true');
    localStorage.setItem('allodos-user', JSON.stringify(loggedInUser));

    if (inviteToken) {
      setInviteToken(null);
      setInvitation(null);
      clearInviteTokenFromUrl();
    }
  };

  const handleLogout = () => {
    clearToken();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setLoginPreFill(null);
    localStorage.removeItem('allodos-auth');
    localStorage.removeItem('allodos-user');
    // Bloco #77: nada da equipe/clínica anterior fica para a próxima conta.
    localStorage.removeItem('allodos-user-profiles');
    localStorage.removeItem('allodos-active-profile-id');
    setUserProfiles([]);
    setStorageScope('');
    setPatients([]);
    setRecordingsList([]);
    setTherapist(defaultTherapist);
    // Reiniciar estados de controle no logout para permitir nova leitura se outro user logar
    setActiveTab('dashboard');
    setSelectedPatientId(null);
  };

  const persistLegalAcknowledgement = async (document: 'terms' | 'lgpd') => {
    if (legalGateBusy) return false;
    setLegalGateBusy(true);
    try {
      const updated = await acknowledgeLegalDocument(document);
      setCurrentUser((prev) => {
        const merged = { ...(prev ?? {}), ...updated } as AuthUser;
        localStorage.setItem('allodos-user', JSON.stringify(merged));
        return merged;
      });
      return true;
    } catch (err) {
      console.error(err);
      alert(`Não foi possível registrar a confirmação. Tente novamente.\n\n${describeError(err)}`);
      return false;
    } finally {
      setLegalGateBusy(false);
    }
  };

  const handleAcceptTerms = () => {
    void persistLegalAcknowledgement('terms');
  };

  const handleAcceptLGPD = async () => {
    const ok = await persistLegalAcknowledgement('lgpd');
    if (!ok) return;
    // Mostrar wizard apenas se nunca viu antes
    const hasSeenWizard = localStorage.getItem('allodos-wizard-seen') === 'true';
    if (!hasSeenWizard) {
      setShowWizard(true);
    }
  };

  const handleCompleteWizard = () => {
    localStorage.setItem('allodos-wizard-seen', 'true');
    setShowWizard(false);
  };

  const handleRestartWizard = () => {
    setShowWizard(true);
  };

  /**
   * O prontuário mudou (salvo/excluído na API pelo editor): reflete no estado
   * global sem recarregar todos os pacientes.
   */
  const handleRecordsChange = (patientId: string, records: ClinicalRecord[]) => {
    setPatients(prev => prev.map(p => (p.id === patientId ? { ...p, records } : p)));
  };

  const handleSaveAIAnalysis = async (patientId: string, recordingId: string, analysis: AudioAnalysis, attachToProntuario: boolean = false) => {
    // Anexar ao prontuário agora persiste na API: antes o registro só existia
    // na memória e sumia na próxima recarga da lista de pacientes.
    let savedRecord: ClinicalRecord | null = null;
    if (attachToProntuario) {
      const formattedContent = `
        <p><strong>Transcrição Literal da Sessão (Supervisor IA - ${analysis.approach}):</strong></p>
        <p>${analysis.transcription.replace(/\n/g, '<br/>')}</p>
        <br/>
        <p><strong>Síntese e Fenômenos Clínicos:</strong></p>
        <p>${analysis.sessionSynthesis.replace(/\n/g, '<br/>')}</p>
        <br/>
        <p><strong>Plano Estratégico de Manejo:</strong></p>
        <p>${analysis.strategicPlan.replace(/\n/g, '<br/>')}</p>
      `.trim();
      try {
        savedRecord = await createClinicalRecord(patientId, {
          date: new Date().toISOString().split('T')[0],
          content: formattedContent,
          approach: analysis.approach,
          aiAnalysis: `${analysis.sessionSynthesis}\n\n${analysis.strategicPlan}`,
        });
      } catch (err) {
        console.error(err);
        alert(`A análise foi salva, mas não foi possível anexá-la ao prontuário: ${describeError(err)}`);
      }
    }

    setPatients(prev => prev.map(p => {
      if (p.id === patientId) {
        const audioAnalyses = p.audioAnalyses ? [...p.audioAnalyses, analysis] : [analysis];
        const records = savedRecord ? [...(p.records || []), savedRecord] : (p.records || []);
        return { ...p, audioAnalyses, records };
      }
      return p;
    }));
    setRecordingsList(prev => prev.map(r => {
      if (r.id === recordingId) {
        return { ...r, transcription: analysis.transcription };
      }
      return r;
    }));
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 3000);
  };

  const handleDeleteCycle = async (patientId: string, cycleId: string) => {
    if (!window.confirm("Deseja realmente excluir este ciclo clínico? Esta ação não pode ser desfeita.")) return;
    try {
      await deleteCycle(patientId, cycleId);
      await refetchPatients();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTranscription = async (recordingId: string) => {
    if (window.confirm("Deseja realmente excluir esta transcrição?")) {
      try {
        await deleteRecordingTranscription(recordingId);
        setRecordingsList(prev => prev.map(r => {
          if (r.id === recordingId) {
            return { ...r, transcription: undefined, transcriptionId: undefined };
          }
          return r;
        }));
      } catch (err) {
        console.error(err);
        alert('Não foi possível excluir a transcrição.');
      }
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingSettings(true);
    try {
      const updated = await updateUserProfile({
        name: therapist.name,
        crp: therapist.crp,
        professionalPhoto: therapist.professionalPhoto,
        whatsapp: therapist.whatsapp ?? '',
        ...(canEditContactWhatsappField ? { clinicWhatsapp: therapist.clinicWhatsapp ?? '' } : {}),
        // A identidade da clínica só sai do administrador (a API também bloqueia).
        ...(canEditClinic
          ? {
              clinicName: therapist.clinicName,
              phone: therapist.phone,
              email: therapist.email?.trim() || undefined,
              website: therapist.website,
              address: therapist.address,
              cnpj: therapist.cnpj,
              avatarUrl: therapist.logo,
            }
          : {}),
      });
      setCurrentUser(updated);
      // Mescla: campos que a API ainda não devolve (ou devolve vazios) não podem
      // apagar o que o usuário acabou de carregar na tela — era o que fazia a
      // foto pessoal sumir depois de "Atualizar Configurações".
      setTherapist((prev) => ({ ...prev, ...therapistFromUser(updated) }));
      localStorage.setItem('allodos-user', JSON.stringify(updated));
      // Bloco #61: a foto nova vai para os perfis da equipe e, com eles, para a
      // Configuração da Agenda (o link externo já lê a foto do perfil na API).
      refetchTeamMembersRef.current?.();
      setShowSettingsToast(true);
      setTimeout(() => setShowSettingsToast(false), 3000);
    } catch (err) {
      console.error(err);
      alert(`Não foi possível salvar as configurações.\n\n${describeError(err)}`);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const logo = await resizeImageToDataUrl(file);
        setTherapist(prev => ({ ...prev, logo }));
      } catch (err) {
        console.error(err);
        alert('Não foi possível processar a imagem. Tente outro arquivo.');
      } finally {
        e.target.value = '';
      }
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Selecione um arquivo de imagem (JPEG ou PNG).');
      return;
    }
    // Bloco 33: a foto ia inteira em base64 e estourava o limite da API
    // (erro "Bad Request"). Agora é redimensionada como o logo da clínica.
    try {
      const dataUrl = await resizeImageToDataUrl(file, 512, 0.82);
      setTherapist(prev => ({ ...prev, professionalPhoto: dataUrl }));
    } catch (err) {
      console.error(err);
      alert('Não foi possível ler a imagem. Tente um arquivo JPEG ou PNG.');
    }
  };

  const handleDownloadPDF = () => {
    const patient = filteredPatients.find(p => p.id === exportPatientId);
    if (!patient) return;

    const doc = new jsPDF();
    doc.setFont('helvetica');

    let y = 20;
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const addHeader = (title: string) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59); // slate-800
      doc.text(title, margin, y);
      y += 6;
      doc.setDrawColor(226, 232, 240); // slate-200
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
    };

    const checkPageOverflow = (neededHeight: number) => {
      if (y + neededHeight > pageHeight - 15) {
        doc.addPage();
        y = 20;
      }
    };

    // 1. HEADER
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(79, 70, 229); // Indigo 600
    doc.text('ALLODOS', margin, y);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.text(`Prontuário Clínico & Cadastro - Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, margin, y);
    y += 15;

    // 2. CADASTRO DO PACIENTE
    addHeader('Informações de Cadastro');

    const infoFields = [
      { label: 'Nome Completo', val: patient.name },
      { label: 'CPF', val: patient.cpf },
      { label: 'Data de Nascimento', val: patient.dob },
      { label: 'Idade', val: patient.age ? `${patient.age} anos` : undefined },
      { label: 'Telefone', val: patient.phone },
      { label: 'E-mail', val: patient.email },
      { label: 'Endereço', val: patient.address },
      { label: 'CEP', val: patient.cep },
      { label: 'Nome da Mãe', val: patient.motherName },
      { label: 'Nome do Pai', val: patient.fatherName },
      { label: 'Contato de Emergência', val: patient.emergencyContact },
      { label: 'Telefone de Emergência', val: patient.emergencyPhone },
      { label: 'Abordagem Psicoterapêutica', val: patient.activeApproach },
      { label: 'Frequência do Ciclo', val: patient.cycleFrequency },
    ];

    doc.setFontSize(9);
    infoFields.forEach((field) => {
      if (field.val) {
        checkPageOverflow(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(71, 85, 105); // slate-600
        doc.text(`${field.label}:`, margin, y);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(15, 23, 42); // slate-900
        doc.text(String(field.val), margin + 50, y);
        y += 7;
      }
    });

    y += 5;

    // 2.1 QUEIXA PRINCIPAL
    if (patient.mainComplaint) {
      checkPageOverflow(25);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(30, 41, 59);
      doc.text('Queixa Principal:', margin, y);
      y += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      const complaintLines = doc.splitTextToSize(patient.mainComplaint, pageWidth - 2 * margin);
      doc.text(complaintLines, margin, y);
      y += (complaintLines.length * 5) + 8;
    }

    // 2.2 MEDICAMENTOS EM USO
    if (patient.medications && patient.medications.length > 0) {
      checkPageOverflow(20 + medicationList(patient.medications).length * 8);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(30, 41, 59);
      doc.text('Medicamentos em Uso:', margin, y);
      y += 6;

      medicationList(patient.medications).forEach((med, idx) => {
        checkPageOverflow(8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        doc.text(`${idx + 1}. ${med.name} - ${med.quantity} (${med.time})`, margin + 4, y);
        y += 6;
      });
      y += 6;
    }

    // 3. EVOLUÇÕES / PRONTUÁRIO
    if (patient.records && patient.records.length > 0) {
      checkPageOverflow(30);
      addHeader('Evoluções e Registro de Sessões');

      const sortedRecords = [...(patient.records ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      sortedRecords.forEach((record) => {
        const titleHeight = 8;
        const contentLines = doc.splitTextToSize(record.content, pageWidth - 2 * margin);
        const contentHeight = contentLines.length * 5;
        
        let aiLines: string[] = [];
        let aiHeight = 0;
        if (record.aiAnalysis) {
          aiLines = doc.splitTextToSize(`Análise de IA: ${record.aiAnalysis}`, pageWidth - 2 * margin - 8);
          aiHeight = (aiLines.length * 4.5) + 6;
        }

        checkPageOverflow(titleHeight + contentHeight + aiHeight + 12);

        // Record Date & Header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(79, 70, 229); // Indigo 600
        doc.text(`Sessão - ${record.date}`, margin, y);
        y += 5.5;

        // Record content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        doc.text(contentLines, margin, y);
        y += contentHeight + 4;

        // AI Analysis
        if (record.aiAnalysis) {
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.5);
          doc.line(margin + 2, y, margin + 2, y + aiHeight - 4);

          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8.5);
          doc.setTextColor(100, 116, 139);
          doc.text(aiLines, margin + 6, y);
          y += aiHeight;
        }

        y += 6; // Spacing between records
      });
    }

    // 4. CICLOS EVOLUTIVOS
    if (patient.cycles && patient.cycles.length > 0) {
      checkPageOverflow(30);
      addHeader('Histórico de Ciclos Evolutivos');

      const sortedCycles = [...(patient.cycles ?? [])].sort((a, b) => {
        const dateA = a.date.split('/').reverse().join('-');
        const dateB = b.date.split('/').reverse().join('-');
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });

      sortedCycles.forEach((cycle) => {
        const titleHeight = 8;
        const notesLines = cycle.clinicalNotes ? doc.splitTextToSize(`Notas Clínicas: ${cycle.clinicalNotes}`, pageWidth - 2 * margin) : [];
        const notesHeight = notesLines.length > 0 ? (notesLines.length * 4.5) + 6 : 0;
        
        checkPageOverflow(titleHeight + notesHeight + 20);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(79, 70, 229);
        doc.text(`Ciclo Evolutivo - ${cycle.date}`, margin, y);
        y += 5.5;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        doc.text(`Prejuízo Funcional: ${cycle.functionalImpairment}/10  |  Sofrimento Subjetivo: ${cycle.subjectiveDistress}/10`, margin, y);
        y += 5;

        if (cycle.scores && cycle.scores.length > 0) {
          const scoresText = (cycle.scores ?? []).map(s => {
            const ind = INDICATORS.find(i => i.id === s.indicatorId);
            return `${ind?.nome || 'Indicador'}: ${s.score}/10`;
          }).join('  |  ');
          
          const scoresLines = doc.splitTextToSize(scoresText, pageWidth - 2 * margin);
          checkPageOverflow(scoresLines.length * 4.5 + 4);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(100, 116, 139);
          doc.text(scoresLines, margin, y);
          y += (scoresLines.length * 4.5) + 2;
        }

        if (cycle.clinicalNotes) {
          y += 2;
          checkPageOverflow(notesHeight);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(51, 65, 85);
          doc.text(notesLines, margin, y);
          y += notesHeight;
        }

        y += 6;
      });
    }

    const fileName = `Prontuario_${patient.name.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
  };

  const handleDownloadCSV = () => {
    const patient = filteredPatients.find(p => p.id === exportPatientId);
    if (!patient) return;

    let csvContent = '\uFEFF'; // UTF-8 BOM to ensure accents render properly in Excel

    const escapeCSV = (val: any) => {
      if (val === null || val === undefined) return '""';
      let str = String(val);
      str = str.replace(/"/g, '""');
      return `"${str}"`;
    };

    csvContent += `"ALLODOS PORTAL - EXPORTAÇÃO DE DADOS DO PACIENTE"\n`;
    csvContent += `"Gerado em:",${escapeCSV(new Date().toLocaleDateString('pt-BR') + ' ' + new Date().toLocaleTimeString('pt-BR'))}\n\n`;

    csvContent += `"INFORMAÇÕES DE CADASTRO"\n`;
    csvContent += `"Nome Completo:",${escapeCSV(patient.name)}\n`;
    csvContent += `"CPF:",${escapeCSV(patient.cpf || 'Não Informado')}\n`;
    csvContent += `"Data de Nascimento:",${escapeCSV(patient.dob || 'Não Informado')}\n`;
    csvContent += `"Idade:",${escapeCSV(patient.age ? `${patient.age} anos` : 'Não Informado')}\n`;
    csvContent += `"Telefone:",${escapeCSV(patient.phone || 'Não Informado')}\n`;
    csvContent += `"E-mail:",${escapeCSV(patient.email || 'Não Informado')}\n`;
    csvContent += `"Endereço:",${escapeCSV(patient.address || 'Não Informado')}\n`;
    csvContent += `"CEP:",${escapeCSV(patient.cep || 'Não Informado')}\n`;
    csvContent += `"Nome da Mãe:",${escapeCSV(patient.motherName || 'Não Informado')}\n`;
    csvContent += `"Nome do Pai:",${escapeCSV(patient.fatherName || 'Não Informado')}\n`;
    csvContent += `"Contato de Emergência:",${escapeCSV(patient.emergencyContact || 'Não Informado')}\n`;
    csvContent += `"Telefone de Emergência:",${escapeCSV(patient.emergencyPhone || 'Não Informado')}\n`;
    csvContent += `"Abordagem Ativa:",${escapeCSV(patient.activeApproach || 'Não Informado')}\n`;
    csvContent += `"Frequência do Ciclo:",${escapeCSV(patient.cycleFrequency || 'Não Informado')}\n`;
    csvContent += `"Queixa Principal:",${escapeCSV(patient.mainComplaint || 'Não Informada')}\n\n`;

    csvContent += `"MEDICAMENTOS EM USO"\n`;
    csvContent += `"Nome do Medicamento","Quantidade / Dose","Horário"\n`;
    const meds = medicationList(patient.medications);
    if (meds.length > 0) {
      meds.forEach(med => {
        csvContent += `${escapeCSV(med.name)},${escapeCSV(med.quantity)},${escapeCSV(med.time)}\n`;
      });
    } else {
      csvContent += `"Nenhum medicamento registrado","",""\n`;
    }
    csvContent += `\n`;

    csvContent += `"HISTÓRICO DE EVOLUÇÕES (PRONTUÁRIO)"\n`;
    csvContent += `"Data","Abordagem","Conteúdo","Análise de IA"\n`;
    if (patient.records && patient.records.length > 0) {
      const sortedRecords = [...(patient.records ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      sortedRecords.forEach(rec => {
        csvContent += `${escapeCSV(rec.date)},${escapeCSV(rec.approach || patient.activeApproach)},${escapeCSV(rec.content)},${escapeCSV(rec.aiAnalysis || 'Sem análise')}\n`;
      });
    } else {
      csvContent += `"Nenhuma evolução registrada","","",""\n`;
    }
    csvContent += `\n`;

    csvContent += `"CICLOS EVOLUTIVOS (HISTÓRICO)"\n`;
    csvContent += `"Data","Prejuízo Funcional (0-10)","Sofrimento Subjetivo (0-10)","Notas Clínicas","Pontuações de Indicadores"\n`;
    if (patient.cycles && patient.cycles.length > 0) {
      const sortedCycles = [...(patient.cycles ?? [])].sort((a, b) => {
        const dateA = a.date.split('/').reverse().join('-');
        const dateB = b.date.split('/').reverse().join('-');
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });
      sortedCycles.forEach(cycle => {
        const scoresText = (cycle.scores ?? []).map(s => {
          const ind = INDICATORS.find(i => i.id === s.indicatorId);
          return `${ind?.nome || 'Indicador'}: ${s.score}/10`;
        }).join(' | ');
        csvContent += `${escapeCSV(cycle.date)},${escapeCSV(cycle.functionalImpairment)},${escapeCSV(cycle.subjectiveDistress)},${escapeCSV(cycle.clinicalNotes)},${escapeCSV(scoresText)}\n`;
      });
    } else {
      csvContent += `"Nenhum ciclo evolutivo registrado","","","",""\n`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    const fileName = `Prontuario_${patient.name.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.csv`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (window.confirm(`Deseja confirmar a assinatura do ${plan}?`)) {
      const newSub = subscriptionService.subscribe(plan);
      setSubscription(newSub);
      alert(`Parabéns! Sua assinatura do ${plan} foi ativada com sucesso.`);
    }
  };

  const handleDeleteAllData = async () => {
    try {
      await Promise.all(recordingsList.map((recording) => deleteRemoteRecording(recording.id)));
      await Promise.all(patients.map((patient) => deletePatient(patient.id)));
      setPatients([]);
      setRecordingsList([]);
      setIsDeleteModalOpen(false);
      setActiveTab('dashboard');
      alert("Todos os dados foram apagados com sucesso.");
    } catch (err) {
      console.error(err);
      alert('Não foi possível apagar todos os dados. Tente novamente.');
    }
  };

  const selectedPatient = useMemo(() => patients.find(p => p.id === selectedPatientId), [patients, selectedPatientId]);

  const handleUpdatePatient = async (updatedPatient: Patient) => {
    try {
      await updatePatient(updatedPatient.id, {
        name: updatedPatient.name,
        dob: updatedPatient.dob,
        phone: updatedPatient.phone,
        email: updatedPatient.email,
        emergencyContact: updatedPatient.emergencyContact,
        emergencyPhone: updatedPatient.emergencyPhone,
        cpf: updatedPatient.cpf,
        cep: updatedPatient.cep,
        address: updatedPatient.address,
        motherName: updatedPatient.motherName,
        fatherName: updatedPatient.fatherName,
        mainComplaint: updatedPatient.mainComplaint,
        medications: normalizeMedications(updatedPatient.medications),
        activeApproach: updatedPatient.activeApproach,
        cycleFrequency: updatedPatient.cycleFrequency,
        // Sem indicador (ex.: paciente do link externo) o cadastro salva mesmo
        // assim; a API recusa lista vazia, então ela só vai quando há seleção.
        ...(updatedPatient.selectedIndicatorIds?.length ? { selectedIndicatorIds: updatedPatient.selectedIndicatorIds } : {}),
        // Só IDs reais de usuário (UUID); perfis locais antigos não existem na API.
        ...(isUuid(updatedPatient.assignedProfessionalId)
          ? { assignedProfessionalId: updatedPatient.assignedProfessionalId }
          : {}),
      });
      await refetchPatients();
    } catch (err) {
      // Antes o erro morria no console e a tela não dava retorno nenhum.
      console.error(err);
      throw err;
    }
  };

  const handleUpdateReport = async (patientId: string, cycle: ClinicalCycle) => {
    if (!isUuid(cycle.id)) return;
    try {
      await updateReportTexts(patientId, cycle.id, {
        patientReport: cycle.patientReport,
        evolutionGuidelines: cycle.evolutionGuidelines,
        clinicalNotes: cycle.clinicalNotes,
        summary: cycle.aiInsight,
      });
      await refetchPatients();
    } catch (err) {
      console.error(err);
    }
  };

  /** Exclusão de um paciente já confirmada no modal da lista (bloco 20). */
  const handleDeletePatientConfirmed = async (id: string) => {
    try {
      await deletePatient(id);
      if (selectedPatientId === id) setSelectedPatientId(null);
      await refetchPatients();
    } catch (err) {
      console.error(err);
      alert(`Não foi possível excluir o paciente.\n\n${describeError(err)}`);
      throw err;
    }
  };

  const handleDeletePatients = async (ids: string[]) => {
    const aviso =
      `Ao jogar fora, TODOS os dados de ${ids.length} paciente(s) serão apagados definitivamente: ` +
      `dados de cadastro, prontuário (evoluções e registros), ciclos clínicos, agendamentos, ` +
      `gravações, transcrições e arquivos anexados.\n\n` +
      `Esta ação não poderá ser desfeita. Deseja continuar?`;
    if (window.confirm(aviso)) {
      try {
        await Promise.all(ids.map((id) => deletePatient(id)));
        if (selectedPatientId && ids.includes(selectedPatientId)) {
          setSelectedPatientId(null);
        }
        await refetchPatients();
      } catch (err) {
        console.error(err);
        alert('Não foi possível excluir os pacientes selecionados.');
      }
    }
  };

  const handleTogglePatientsStatus = async (ids: string[], newStatus: 'Ativo' | 'Inativo') => {
    // Atualização otimista: o painel reage na hora e a API confirma em seguida.
    setPatients(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: newStatus } : p));
    if (newStatus === 'Inativo' && selectedPatientId && ids.includes(selectedPatientId)) {
      setSelectedPatientId(null);
    }
    try {
      await setPatientsStatus(ids, newStatus);
      await refetchPatients();
    } catch (err) {
      console.error(err);
      alert('Não foi possível atualizar o status dos pacientes.');
      await refetchPatients();
    }
  };

  const handleAddPatient = async (newPatient: Patient) => {
    setNewPatientError(null);

    if (!newPatient.selectedIndicatorIds?.length) {
      setNewPatientError('Selecione pelo menos um indicador para criar o paciente.');
      return;
    }

    setIsSavingPatient(true);
    try {
      const payload: CreatePatientPayload = {
        name: newPatient.name,
        dob: newPatient.dob,
        phone: newPatient.phone,
        email: newPatient.email,
        emergencyContact: newPatient.emergencyContact,
        emergencyPhone: newPatient.emergencyPhone,
        cpf: newPatient.cpf,
        cep: newPatient.cep,
        address: newPatient.address,
        motherName: newPatient.motherName,
        fatherName: newPatient.fatherName,
        mainComplaint: newPatient.mainComplaint,
        medications: normalizeMedications(newPatient.medications),
        activeApproach: newPatient.activeApproach,
        cycleFrequency: newPatient.cycleFrequency,
        assignedProfessionalId: newPatient.assignedProfessionalId,
        selectedIndicatorIds: newPatient.selectedIndicatorIds,
      };
      await createPatient(payload);
      setIsNewPatientModalOpen(false);
      await refetchPatients();
    } catch (err) {
      // Antes o erro só ia para o console: o usuário clicava em "Criar Paciente"
      // e nada acontecia na tela.
      console.error(err);
      setNewPatientError(describeError(err));
    } finally {
      setIsSavingPatient(false);
    }
  };

  const handleSaveCycle = async (patientId: string, cycle: ClinicalCycle) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    setIsGeneratingIA(true);
    setCycleError(null);
    let payload: {
      date: string;
      approach: ClinicalCycle['approach'];
      clinicalNotes: string;
      functionalImpairment: number;
      subjectiveDistress: number;
      copingResources: number;
      scores: { indicatorId: string; score: number }[];
      aiInsight?: string;
      patientReport?: string;
      evolutionGuidelines?: string;
    };
    try {
      payload = {
        date: cycle.date,
        approach: cycle.approach,
        clinicalNotes: cycle.clinicalNotes,
        functionalImpairment: cycle.functionalImpairment,
        subjectiveDistress: cycle.subjectiveDistress,
        copingResources: cycle.copingResources ?? 5,
        scores: cycle.scores.map((s) => ({ indicatorId: s.indicatorId, score: s.score })),
        aiInsight: cycle.aiInsight,
        patientReport: cycle.patientReport,
        evolutionGuidelines: cycle.evolutionGuidelines,
      };
      const persistedCycle = isUuid(cycle.id)
        ? await updateCycle(patientId, cycle.id, payload)
        : await createCycle(patientId, payload);

      setPatients(prev => prev.map((candidate) => {
        if (candidate.id !== patientId) return candidate;
        const existingCycles = candidate.cycles ?? [];
        const hasCycle = existingCycles.some((item) => item.id === persistedCycle.id);
        return {
          ...candidate,
          cycles: hasCycle
            ? existingCycles.map((item) => item.id === persistedCycle.id ? persistedCycle : item)
            : [...existingCycles, persistedCycle],
        };
      }));

      void (async () => {
        const previousCycle = patient.cycles
          ?.filter((candidate) => candidate.id !== persistedCycle.id && candidate.id !== cycle.id)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .slice(-1)[0];
        // A interpretação da IA é um enriquecimento, não pré-requisito: se ela
        // falhar, o ciclo ainda precisa ser registrado. Antes, um erro da IA
        // abortava tudo e o ciclo simplesmente não aparecia na lista.
        try {
          await registerAiConsent(patientId);
          const aiResults = await interpretCycle({
            patientId,
            approach: cycle.approach,
            date: cycle.date,
            subjectiveDistress: cycle.subjectiveDistress,
            functionalImpairment: cycle.functionalImpairment,
            clinicalNotes: cycle.clinicalNotes,
            indicators: (cycle.scores ?? []).map((s) => {
              const ind = INDICATORS.find((i) => i.id === s.indicatorId);
              return {
                name: ind?.nome || s.indicatorId,
                axis: ind?.eixo || 'unknown',
                score: s.score,
              };
            }),
            previousCycleSummary: previousCycle
              ? `Data ${previousCycle.date}; sofrimento ${previousCycle.subjectiveDistress}/10; prejuízo funcional ${previousCycle.functionalImpairment}/10; notas: ${previousCycle.clinicalNotes || 'sem notas'}.`
              : undefined,
          });
          const enrichedCycle = await updateCycle(patientId, persistedCycle.id, {
            ...payload,
            aiInsight: aiResults.clinicalInsight,
            patientReport: aiResults.patientReport,
            evolutionGuidelines: aiResults.evolutionGuidelines,
          });
          setPatients(prev => prev.map((candidate) => {
            if (candidate.id !== patientId) return candidate;
            return {
              ...candidate,
              cycles: (candidate.cycles ?? []).map((item) => item.id === enrichedCycle.id ? enrichedCycle : item),
            };
          }));
        } catch (aiErr) {
          console.error('Falha na interpretação por IA do ciclo:', aiErr);
          setCycleError(
            `O ciclo foi registrado, mas a análise por IA falhou: ${describeError(aiErr)}`,
          );
        } finally {
          try {
            await refetchPatients();
          } finally {
            setIsGeneratingIA(false);
          }
        }
      })();
    } catch (err) {
      // Antes o erro só ia ao console: o usuário via a análise rodar e o ciclo
      // nunca aparecia, sem nenhuma explicação.
      console.error(err);
      setIsGeneratingIA(false);
      setCycleError(`Não foi possível registrar o ciclo: ${describeError(err)}`);
      throw err;
    }
  };

  const navigateToPatient = (id: string, autoEdit = false) => {
    setSelectedPatientId(id);
    setAutoOpenEditProfile(autoEdit);
    setActiveTab('cycles');
  };

  const getInitials = (name: string) => {
    const parts = (name || '').trim().split(' ');
    if (parts.length === 0 || !parts[0]) return '??';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };

  const headerTitle = useMemo(() => {
    switch(activeTab) {
      case 'dashboard': return 'Dashboard Clínico';
      case 'patients': return 'Gestão de Pacientes';
      case 'cycles': return 'Gestão de Ciclos e Evolução';
      case 'assistant': return 'Assistente de Supervisão IA';
      case 'recordings': return 'Gravações';
      case 'ai-analysis': return 'Análise Clínica IA';
      case 'transcriptions': return 'Transcrições de Sessões';
      case 'agenda': return 'Agenda e Sessões';
      case 'agenda-settings': return 'Configurações da Agenda';
      case 'waitlist': return 'Lista de Espera / Férias';
      case 'teleconsultation': return 'Teleconsulta Segura';
      case 'finance': return 'Gestão Financeira';
      case 'repository': return 'Repositório de Documentos';
      case 'subscriptions': return 'Plano e Assinaturas';
      case 'settings': return 'Configurações do Consultório';
      case 'user-profiles': return 'Perfis de Usuário';
      default: return 'Allodos';
    }
  }, [activeTab]);

  // Portal público do paciente: `/?agendar=<slug>` é aberto sem login, antes de
  // qualquer verificação de sessão.
  if (bookingSlug) {
    return <PatientPortal slug={bookingSlug} />;
  }

  // Links do WhatsApp para o paciente: `/?consulta=<token>&acao=confirmar|cancelar|reagendar`.
  if (appointmentAction) {
    return <AppointmentAction token={appointmentAction.token} action={appointmentAction.action} />;
  }

  // Bloco 39: link do e-mail de recuperação (`/reset-password?token=` ou `?reset=`),
  // antes de qualquer sessão — a senha é criada pelo próprio usuário.
  if (resetToken && !isAuthenticated) {
    return (
      <ResetPassword
        token={resetToken}
        onDone={() => { clearResetTokenFromUrl(); setResetToken(null); }}
        onRequestNew={() => { clearResetTokenFromUrl(); setResetToken(null); setOpenForgot(true); }}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <Login
        onLogin={handleLogin}
        initialEmail={loginPreFill?.email}
        initialName={loginPreFill?.name}
        initialIsRegister={loginPreFill?.isRegister}
        inviteToken={inviteToken}
        inviteRoleLabel={invitation?.roleLabel ?? null}
        initialForgot={openForgot}
      />
    );
  }
  
  // SEQUÊNCIA DE BLOQUEIOS OBRIGATÓRIOS (por usuário, para todos os perfis)
  if (legalStatusKnown && !hasAcceptedTerms) return <TermsOfUse onAccept={handleAcceptTerms} />;
  if (legalStatusKnown && !hasAcceptedLGPD) return <LGPDChecklist onAccept={handleAcceptLGPD} />;

  const isTrial = subscription.plan === SubscriptionPlan.FREE_TRIAL;

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300 overflow-hidden">
      {/* EXIBIÇÃO DO WIZARD */}
      {showWizard && <OnboardingWizard onComplete={handleCompleteWizard} />}

      {/* Backdrop for mobile */}
      {!isSidebarCollapsed && (
        <div 
          onClick={() => setIsSidebarCollapsed(true)} 
          className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-200"
        />
      )}

      <aside className={`bg-indigo-900 dark:bg-slate-900 text-white flex flex-col shrink-0 transition-all duration-300 shadow-xl z-50
        fixed inset-y-0 left-0 w-64 lg:static lg:flex h-full
        ${isSidebarCollapsed 
          ? '-translate-x-full lg:w-0 lg:opacity-0 lg:pointer-events-none' 
          : 'translate-x-0 lg:w-64 lg:opacity-100 lg:pointer-events-auto'
        }
      `}>
        <div className="px-6 py-8">
          <AllodosLogo variant="gray" className="w-[155px] h-auto" />
        </div>
        <nav
          className="flex-1 px-4 mt-4 overflow-y-auto pb-8 custom-scrollbar"
          onClick={(e) => {
            // O chevron dos submenus interrompe a propagação: só fecha ao navegar.
            if ((e.target as HTMLElement).closest('button')) closeSidebarOnMobile();
          }}
        >
          <ul className="space-y-1">
            <SidebarItem icon={<LayoutDashboard size={18} />} label="Painel" active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} />
            
            {activeProfile.permissions?.patients !== false && (
              <SidebarItem icon={<Users size={18} />} label="Pacientes" active={activeTab === 'patients' || activeTab === 'cycles'} onClick={() => { setSelectedPatientId(null); setFilterAlertsOnly(false); setActiveTab('patients'); }} />
            )}
            
            {activeProfile.permissions?.supervisorChat !== false && (
              <SidebarItem icon={<MessageSquareText size={18} />} label="Supervisor Chat" active={activeTab === 'assistant'} onClick={() => setActiveTab('assistant')} />
            )}
            
            <div className="my-6 border-t border-white/10 mx-3"></div>
            
            {activeProfile.permissions?.agenda !== false && (
              <>
                <SidebarItem 
                  icon={<Calendar size={18} />} 
                  label="Agenda" 
                  active={activeTab === 'agenda' || activeTab === 'agenda-settings' || activeTab === 'waitlist'} 
                  onClick={() => {
                    setActiveTab('agenda');
                    setIsAgendaMenuOpen(!isAgendaMenuOpen);
                  }} 
                  rightIcon={
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAgendaMenuOpen(!isAgendaMenuOpen);
                      }}
                      className="p-1 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                    >
                      <ChevronDown 
                        size={16} 
                        className={`transform transition-transform duration-300 ${isAgendaMenuOpen ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  }
                />
                {isAgendaMenuOpen && canManageBookingPage(activeProfile.role) && (
                  /* Bloco 36: só administrador e recepção configuram a agenda. */
                  <li className="pl-6 animate-in slide-in-from-top-2 duration-300">
                    <button
                      onClick={() => setActiveTab('agenda-settings')}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                        activeTab === 'agenda-settings' 
                          ? 'bg-white/15 text-white shadow-md' 
                          : 'text-indigo-300/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <SettingsIcon size={14} className={activeTab === 'agenda-settings' ? 'text-white' : 'text-indigo-400'} />
                      Configurações da Agenda
                    </button>
                    {/* Bloco #64: triagem dos inscritos durante o recesso */}
                    <button
                      onClick={() => setActiveTab('waitlist')}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all mt-1 ${
                        activeTab === 'waitlist'
                          ? 'bg-white/15 text-white shadow-md'
                          : 'text-indigo-300/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <CalendarClock size={14} className={activeTab === 'waitlist' ? 'text-white' : 'text-indigo-400'} />
                      Lista de Espera / Férias
                    </button>
                  </li>
                )}
              </>
            )}

            {activeProfile.permissions?.finance !== false && (
              <SidebarItem icon={<Wallet size={18} />} label="Financeiro" active={activeTab === 'finance'} onClick={() => setActiveTab('finance')} />
            )}
            
            {activeProfile.permissions?.teleconsultation !== false && (
              <>
                <div className="my-6 border-t border-white/10 mx-3"></div>
                <SidebarItem icon={<Video size={18} />} label="Teleconsulta" active={activeTab === 'teleconsultation'} onClick={() => setActiveTab('teleconsultation')} />
              </>
            )}
            
            {activeProfile.permissions?.repository !== false && (
              <>
                <div className="my-6 border-t border-white/10 mx-3"></div>
                <SidebarItem icon={<FolderOpen size={18} />} label="Repositório" active={activeTab === 'repository'} onClick={() => setActiveTab('repository')} />
              </>
            )}

            {activeProfile.permissions?.subscriptions !== false && (
              <SidebarItem icon={<CreditCard size={18} />} label="Assinatura" active={activeTab === 'subscriptions'} onClick={() => setActiveTab('subscriptions')} />
            )}

            {activeProfile.permissions?.settings !== false && (
              <>
                <SidebarItem 
                  icon={<SettingsIcon size={18} />} 
                  label="Configurações" 
                  active={activeTab === 'settings' || activeTab === 'user-profiles'} 
                  onClick={() => {
                    setActiveTab('settings');
                    setIsSettingsMenuOpen(!isSettingsMenuOpen);
                  }} 
                  rightIcon={
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSettingsMenuOpen(!isSettingsMenuOpen);
                      }}
                      className="p-1 hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                    >
                      <ChevronDown 
                        size={16} 
                        className={`transform transition-transform duration-300 ${isSettingsMenuOpen ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  }
                />
                {isSettingsMenuOpen && (
                  <li className="pl-6 animate-in slide-in-from-top-2 duration-300">
                    {activeProfile.role === 'Administrador' && (
                      <button
                        onClick={() => setActiveTab('user-profiles')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                          activeTab === 'user-profiles' 
                            ? 'bg-white/15 text-white shadow-md' 
                            : 'text-indigo-300/70 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <UserIcon size={14} className={activeTab === 'user-profiles' ? 'text-white' : 'text-indigo-400'} />
                        Perfis de Usuário
                      </button>
                    )}
                    {/* Administração do Allodos (produto): o painel fica no backoffice, em outro subdomínio. */}
                    {currentUser?.isPlatformAdmin && (
                      <a
                        href={import.meta.env.VITE_BACKOFFICE_URL || 'https://admin.allodos.com'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all text-indigo-300/70 hover:bg-white/5 hover:text-white"
                      >
                        <Users size={14} className="text-indigo-400" />
                        Backoffice Allodos
                      </a>
                    )}
                  </li>
                )}
              </>
            )}
          </ul>
        </nav>
        <div className="p-6 border-t border-white/10 shrink-0 relative">
          <div 
            className="flex items-center gap-3 mb-5 p-2 -mx-2 rounded-2xl select-none"
          >
            {/* Foto pessoal do profissional (Configurações); cai para a logo da
                clínica e, por fim, para as iniciais. */}
            {sidebarAvatar ? (
              <img
                src={sidebarAvatar}
                alt={sidebarName}
                className="w-11 h-11 rounded-2xl object-cover shadow-lg border border-white/25 shrink-0"
              />
            ) : (
              <div className="w-11 h-11 rounded-2xl bg-indigo-500 text-white flex items-center justify-center font-black uppercase shadow-lg border border-white/25 shrink-0">
                {getInitials(sidebarName)}
              </div>
            )}
            <div className="overflow-hidden flex-1">
              <p className="text-[11px] font-black uppercase tracking-widest truncate text-white">{sidebarName}</p>
              <p className="text-[10px] font-black text-indigo-300/80 truncate uppercase tracking-wider">
                {therapist.crp ? `${activeProfile.role} · CRP ${therapist.crp}` : activeProfile.role}
              </p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-indigo-200 hover:text-white transition-all group"><LogOut size={12} className="group-hover:-translate-x-1 transition-transform" /> Sair da Conta</button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950 transition-colors duration-300 relative">
        {showSaveToast && (
          <div className="fixed bottom-4 sm:bottom-auto sm:top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-auto z-[100] bg-emerald-600 text-white px-5 sm:px-8 py-3.5 rounded-3xl shadow-2xl font-black uppercase tracking-widest text-xs flex items-center gap-3 animate-in slide-in-from-top-4">
            <CheckCircle size={18} /> Análise Salva em Áudios e transcrições
          </div>
        )}

        {/* Falhas ao registrar ciclo agora aparecem na tela, e não só no console. */}
        {cycleError && (
          <div className="fixed bottom-4 sm:bottom-auto sm:top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] sm:w-auto z-[100] max-w-lg bg-rose-600 text-white px-6 py-4 rounded-3xl shadow-2xl text-xs font-bold flex items-start gap-3 animate-in slide-in-from-top-4">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span className="leading-relaxed">{cycleError}</span>
            <button
              onClick={() => setCycleError(null)}
              className="ml-2 shrink-0 font-black uppercase tracking-widest text-[10px] opacity-80 hover:opacity-100"
            >
              Fechar
            </button>
          </div>
        )}

        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 sm:px-10 py-4 sm:py-6 flex justify-between items-center gap-3 sticky top-0 z-20 transition-colors duration-300">
          <div className="flex flex-wrap items-center gap-3 sm:gap-6 min-w-0">
            <button 
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl text-slate-600 dark:text-slate-300 transition-colors shadow-sm"
              title={isSidebarCollapsed ? "Expandir Menu" : "Recuar Menu"}
            >
              <Menu size={20} />
            </button>
            <div>
              <h2 className="text-base sm:text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{headerTitle}</h2>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mt-0.5">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            {activeTab === 'agenda' && (
              <div className="flex flex-wrap items-center gap-2 ml-2 sm:ml-4">
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('agenda-action', { detail: 'appointment' }))}
                  className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md shadow-emerald-700/10 flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus size={12} className="stroke-[3]" />
                  Novo agendamento
                </button>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('agenda-action', { detail: 'compromise' }))}
                  className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md shadow-amber-700/10 flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus size={12} className="stroke-[3]" />
                  Compromissos
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {/* Botão de Suporte WhatsApp */}
            <a
              href={`https://wa.me/555431966797?text=${encodeURIComponent("Olá, venho do Allodos e preciso de suporte.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-slate-800 dark:hover:bg-slate-750 dark:text-indigo-400 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-sm border border-indigo-100 dark:border-indigo-900/30 flex items-center gap-2 cursor-pointer hover:scale-105 active:scale-95"
              title="Solicitar Suporte"
            >
              <HelpCircle size={14} className="stroke-[2.5]" />
              <span className="hidden sm:inline">Suporte</span>
            </a>

            {activeTab === 'cycles' && selectedPatientId ? (
              <button 
                onClick={() => { setSelectedPatientId(null); setActiveTab('patients'); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-2xl flex items-center gap-2 text-xs font-black uppercase tracking-widest shadow-xl transition-all hover:scale-105 active:scale-95"
              >
                <ArrowLeft size={16} /> Voltar
              </button>
            ) : activeTab === 'patients' && (
              /* "Novo Paciente" vive só na aba Pacientes — no Painel poluía a visão geral. */
              <button onClick={() => setIsNewPatientModalOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-6 py-2.5 rounded-2xl flex items-center gap-2 text-xs font-black uppercase tracking-widest shadow-xl transition-all" title="Novo Paciente"><UserPlus size={18} /> <span className="hidden sm:inline">Novo Paciente</span></button>
            )}
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-10">
          {activeTab === 'dashboard' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-2">
                <StatCard title="Total de Pacientes" value={dashboardStats.totalPatients.toString()} icon={<Users className="text-blue-500" />} />
                <StatCard title={`Ciclos Concluídos`} value={dashboardStats.totalCycles.toString()} icon={<Clipboard className="text-green-500" />} />
                <StatCard 
                  title="Alertas Ativos" 
                  value={dashboardStats.activeAlerts.toString()} 
                  icon={<AlertCircle className="text-amber-500" />} 
                  onClick={() => {
                    setFilterAlertsOnly(true);
                    setActiveTab('patients');
                  }}
                />
                <StatCard title="Taxa de Evolução" value={dashboardStats.evolutionRate} icon={<TrendingUp className="text-indigo-500" />} />
              </div>
              <div className="col-span-full lg:col-span-2">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h3 className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em] h-fit">Pacientes Recentes</h3>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input 
                      type="text" 
                      placeholder="Pesquisar paciente..." 
                      className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs shadow-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 transition-all dark:text-slate-200"
                      value={dashboardSearchTerm}
                      onChange={(e) => setDashboardSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  {(dashboardSearchTerm
                    ? activePatients.filter(p => p.name.toLowerCase().includes(dashboardSearchTerm.toLowerCase()))
                    : activePatients.slice(0, 5)
                  ).map(p => (
                    <PatientRow 
                      key={p.id} 
                      patient={p} 
                      onClick={() => setActiveTab('patients')} 
                      onEditClick={() => setActiveTab('patients')} 
                      getInitials={getInitials} 
                    />
                  ))}
                  {dashboardSearchTerm && activePatients.filter(p => p.name.toLowerCase().includes(dashboardSearchTerm.toLowerCase())).length === 0 && (
                    <p className="text-xs font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest text-center py-6 italic">Nenhum paciente encontrado.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'patients' && (
            <PatientList 
              patients={filteredPatients} 
              onSelectPatient={navigateToPatient} 
              therapist={therapist} 
              filterAlertsOnly={filterAlertsOnly}
              onClearFilterAlerts={() => setFilterAlertsOnly(false)}
              onDeletePatients={handleDeletePatients}
              onDeletePatient={handleDeletePatientConfirmed}
              professionals={clinicProfessionals}
              showProfessionalFilter={activeProfile.role === 'Administrador'}
              onTogglePatientsStatus={handleTogglePatientsStatus}
            />
          )}
          {activeTab === 'cycles' && selectedPatientId && selectedPatient ? (
            <PatientDetails 
              patient={selectedPatient} 
              onBack={() => { setSelectedPatientId(null); setActiveTab('patients'); }} 
              onSaveCycle={(cycle) => handleSaveCycle(selectedPatient.id, cycle)} 
              onUpdatePatient={handleUpdatePatient}
              onUpdateReport={(cycle) => handleUpdateReport(selectedPatient.id, cycle)}
              onDeleteCycle={(cycleId) => handleDeleteCycle(selectedPatient.id, cycleId)}
              therapist={therapist} 
              isGeneratingIA={isGeneratingIA} 
              patients={filteredPatients}
              recordingsList={recordingsList}
              setRecordingsList={setRecordingsList}
              onSaveAIAnalysis={handleSaveAIAnalysis}
              onDeleteTranscription={handleDeleteTranscription}
              onRecordsChange={handleRecordsChange}
              initialOpenEditProfile={autoOpenEditProfile}
              onClearInitialOpenEditProfile={() => setAutoOpenEditProfile(false)}
              initialSubTab={initialSubTab}
              onClearInitialSubTab={() => setInitialSubTab(undefined)}
              initialProntuarioDate={autoOpenProntuarioDate}
              onClearInitialProntuarioDate={() => setAutoOpenProntuarioDate(undefined)}
            />
          ) : activeTab === 'cycles' && (
            <PatientList 
              patients={filteredPatients} 
              onSelectPatient={navigateToPatient} 
              therapist={therapist} 
              onDeletePatients={handleDeletePatients}
              onDeletePatient={handleDeletePatientConfirmed}
              onTogglePatientsStatus={handleTogglePatientsStatus}
              professionals={clinicProfessionals}
              showProfessionalFilter={activeProfile.role === 'Administrador'}
            />
          )}

          {activeTab === 'assistant' && <ClinicalAssistant patientId={selectedPatient?.id || activePatients[0]?.id} />}
          {activeTab === 'recordings' && (
            <Recordings
              patients={activePatients}
              recordings={recordingsList}
              onRecordingsChange={setRecordingsList}
              currentPatientId={selectedPatientId || undefined}
              onTriggerAIAnalysis={(recordingId) => { setPendingRecordingId(recordingId); setActiveTab('ai-analysis'); }}
              onTriggerTranscription={() => setActiveTab('transcriptions')}
            />
          )}
          {activeTab === 'ai-analysis' && (
            <AIAnalysis
              patients={activePatients}
              recordings={recordingsList}
              onSaveToPatient={handleSaveAIAnalysis}
              initialRecordingId={pendingRecordingId}
            />
          )}
          {activeTab === 'transcriptions' && <Transcriptions recordings={recordingsList} onRecordingsChange={setRecordingsList} onDeleteTranscription={handleDeleteTranscription} />}
          {activeTab === 'agenda' && <Agenda patients={activePatients} userProfiles={userProfiles} activeProfile={activeProfile} />}
          {activeTab === 'agenda-settings' && canManageBookingPage(activeProfile.role) && <AgendaSettings userProfiles={userProfiles} activeProfile={activeProfile} clinicName={therapist.clinicName || therapist.name} />}
          {activeTab === 'waitlist' && canManageBookingPage(activeProfile.role) && (
            <Waitlist
              professionals={clinicProfessionals}
              defaultProfessionalId={activeProfile.role === 'Administrador' ? String(activeProfile.id) : ''}
              onPatientCreated={() => void refetchPatients()}
              onSchedule={() => setActiveTab('agenda')}
            />
          )}
          {activeTab === 'teleconsultation' && <Teleconsultation />}
          {activeTab === 'finance' && <Finance patients={activePatients} serviceNames={serviceNames} professionals={clinicProfessionals} activeProfile={activeProfile} />}
          {activeTab === 'repository' && <Repository />}
          {activeTab === 'subscriptions' && <SubscriptionManagement subscription={subscription} onSelectPlan={handleSelectPlan} />}
          

          {activeTab === 'user-profiles' && activeProfile.role === 'Administrador' && (
            <div className="max-w-4xl mx-auto pb-20 space-y-8 animate-in fade-in duration-500">
               <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden transition-colors">
                <div className="bg-indigo-900 p-5 sm:p-10 text-white flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="bg-white/10 p-4 rounded-3xl"><UserIcon size={28} /></div>
                    <div>
                      <h3 className="text-3xl font-black tracking-tight">Perfis de Usuário</h3>
                      <p className="text-indigo-300 text-[10px] font-black uppercase tracking-[0.2em] mt-1.5">Gerencie os acessos e permissões do consultório</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 sm:p-12 space-y-10">
                  {/* Lista de Perfis */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <UserIcon className="text-indigo-500" size={20} />
                        <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Perfis Cadastrados</h4>
                      </div>
                      <button 
                        onClick={openAddProfileModal}
                        className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md shadow-indigo-600/10 flex items-center gap-1.5 cursor-pointer"
                      >
                        <Plus size={12} className="stroke-[3]" /> Adicionar Perfil
                      </button>
                    </div>

                    {teamNotice && (
                      <div
                        role="status"
                        aria-live="polite"
                        className={`px-5 py-4 rounded-2xl text-xs font-bold flex items-start gap-2 ${
                          teamNotice.ok
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/50'
                            : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-900/50'
                        }`}
                      >
                        {teamNotice.ok ? <CheckCircle size={16} className="shrink-0" /> : <AlertCircle size={16} className="shrink-0" />}
                        <span>{teamNotice.msg}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                      {userProfiles.map(profile => (
                        <div 
                          key={profile.id}
                          className="p-6 bg-slate-50/50 dark:bg-slate-950/20 rounded-[2rem] border border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all hover:border-slate-200 dark:hover:border-slate-700"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-black uppercase">
                              {getInitials(profile.name)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2.5">
                                <p className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tight">{profile.name}</p>
                                <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                  profile.status === 'Ativo' 
                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/25 dark:text-emerald-400' 
                                    : profile.status === 'Aguardando Cadastro'
                                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/25 dark:text-amber-400'
                                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                                }`}>
                                  {profile.status}
                                </span>
                              </div>
                              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">{profile.role} • {profile.email}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 self-end sm:self-auto">
                            {profile.status === 'Aguardando Cadastro' && (
                              <button
                                onClick={() => handleCopyInvite(profile)}
                                className="p-2.5 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900 text-amber-700 dark:text-amber-400 rounded-xl border border-amber-200/50 dark:border-amber-800 transition-all cursor-pointer shadow-sm flex items-center gap-1.5"
                                title="Copiar Link de Convite"
                              >
                                <Clipboard size={12} />
                                <span className="text-[8px] font-black uppercase tracking-widest">Copiar Convite</span>
                              </button>
                            )}
                            <button
                              onClick={() => openEditProfileModal(profile)}
                              className="p-2.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 text-slate-600 dark:text-slate-300 rounded-xl border border-slate-200/50 dark:border-slate-700 transition-all cursor-pointer shadow-sm"
                              title="Editar Perfil"
                            >
                              <Pencil size={12} />
                            </button>
                            {/* Bloco #58: só o administrador exclui, e só Profissional/Recepcionista. */}
                            {activeProfile?.role === 'Administrador' &&
                              (profile.role === 'Profissional' || profile.role === 'Recepcionista') &&
                              profile.id !== currentUser?.id && (
                              <button
                                onClick={() => setProfileToDelete(profile)}
                                className="p-2.5 bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-950/25 dark:text-red-400 rounded-xl border border-red-100/50 dark:border-red-950 transition-all cursor-pointer shadow-sm"
                                title="Excluir Perfil"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Bloco #58: confirmação da exclusão, com aviso de backup */}
                      {profileToDelete && (
                        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center z-[130] p-4" role="dialog" aria-modal="true" aria-labelledby="delete-profile-title">
                          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 sm:p-8 rounded-[2.5rem] shadow-2xl w-full max-w-md space-y-6">
                            <div className="flex items-start gap-4">
                              <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 shrink-0"><Trash2 size={22} /></div>
                              <div className="space-y-2">
                                <h4 id="delete-profile-title" className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">Excluir {profileToDelete.name}?</h4>
                                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 leading-relaxed">
                                  Tem certeza de que deseja excluir este usuário? Recomendamos fazer o backup das informações cadastradas e vinculadas a este perfil antes de continuar. Esta operação deve ser confirmada para excluir definitivamente o usuário.
                                </p>
                                <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500">
                                  {profileToDelete.role} · {profileToDelete.email}
                                  {profileToDelete.invitationId
                                    ? ' · o convite pendente será cancelado.'
                                    : ' · pacientes, agenda e registros da clínica ficam com o administrador.'}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col-reverse sm:flex-row gap-3">
                              <button
                                type="button"
                                disabled={isDeletingProfile}
                                onClick={() => setProfileToDelete(null)}
                                className="flex-1 py-3.5 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-2xl text-[11px] font-black uppercase tracking-wider disabled:opacity-50"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                disabled={isDeletingProfile}
                                onClick={confirmDeleteProfile}
                                className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl text-[11px] font-black uppercase tracking-wider disabled:opacity-50"
                              >
                                {isDeletingProfile ? 'Excluindo...' : 'Confirmar exclusão'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pop-up de Adição / Edição de Perfil */}
                      {isProfileModalOpen && (
                        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[110] flex items-center justify-center p-4">
                          <form 
                            onSubmit={handleSaveProfile}
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.15)] flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
                          >
                            {/* Header do Modal */}
                            <div className="bg-indigo-900 p-5 sm:p-8 text-white">
                              <div className="flex items-center gap-4">
                                <div className="bg-white/15 p-3 rounded-2xl">
                                  <UserIcon size={20} />
                                </div>
                                <div>
                                  <h4 className="text-xl font-black uppercase tracking-wider">{editingProfile ? 'Configurar Perfil' : 'Novo Perfil de Usuário'}</h4>
                                  <p className="text-indigo-300 text-[10px] font-bold uppercase tracking-widest mt-1">Configure o tipo e privilégios de acesso</p>
                                </div>
                              </div>
                            </div>

                            {/* Corpo do Modal (Scrollable) */}
                            <div className="p-5 sm:p-8 space-y-6 overflow-y-auto custom-scrollbar flex-1 text-left">
                              {/* Nome Completo */}
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Nome Completo</label>
                                <input 
                                  type="text"
                                  required
                                  placeholder="Ex: Dra. Ana Paula Santos"
                                  value={profileForm.name}
                                  onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                                />
                              </div>

                              {/* Email */}
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">E-mail do Usuário</label>
                                <input 
                                  type="email"
                                  required
                                  placeholder="Ex: ana.santos@consultorio.com"
                                  value={profileForm.email}
                                  onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                {/* Tipo de Perfil */}
                                <div className="space-y-2">
                                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Tipo de Perfil</label>
                                  <select 
                                    value={profileForm.role}
                                    onChange={(e) => handleRoleChangeInForm(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                                  >
                                    <option value="Administrador">Administrador</option>
                                    <option value="Profissional">Profissional</option>
                                    <option value="Recepcionista">Recepcionista</option>
                                  </select>
                                </div>

                                {/* Status */}
                                <div className="space-y-2">
                                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Status da Conta</label>
                                  <select 
                                    value={profileForm.status}
                                    onChange={(e) => setProfileForm(prev => ({ ...prev, status: e.target.value }))}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 rounded-xl text-xs font-bold text-slate-800 dark:text-slate-100 cursor-pointer"
                                  >
                                    <option value="Ativo">Ativo</option>
                                    <option value="Inativo">Inativo</option>
                                  </select>
                                </div>
                              </div>

                              {/* Space/Separation instead of privileges */}
                              <div className="pt-2 border-t border-slate-100 dark:border-slate-800"></div>
                            </div>

                            {/* Footer do Modal */}
                            <div className="px-5 sm:px-8 py-5 bg-slate-50 dark:bg-slate-850 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
                              <button
                                type="button"
                                onClick={() => setIsProfileModalOpen(false)}
                                className="px-6 py-3 border border-slate-200 dark:border-slate-750 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all cursor-pointer"
                              >
                                Cancelar
                              </button>
                              <button
                                type="submit"
                                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md shadow-indigo-600/10 cursor-pointer"
                              >
                                Salvar Perfil
                              </button>
                            </div>
                          </form>
                        </div>
                      )}

                      {/* Modal de Confirmação de Convite Enviado */}
                      {invitedProfile && (
                        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[120] flex items-center justify-center p-4">
                          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] w-full max-w-md overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.15)] flex flex-col p-5 sm:p-8 text-left animate-in zoom-in-95 duration-200">
                            <div className="mx-auto w-16 h-16 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mb-6">
                              <Mail size={32} />
                            </div>
                            
                            <h4 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight text-center mb-2">
                              {invitedProfile.emailDelivery === 'not-configured' ? 'Convite criado' : 'E-mail de Convite Enviado!'}
                            </h4>
                            {invitedProfile.emailDelivery === 'not-configured' ? (
                              <div className="mb-6 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                                O envio de e-mail ainda não está configurado no servidor (SMTP), então a mensagem
                                <strong> não foi entregue</strong> em <strong>{invitedProfile.email}</strong>. Copie o link exclusivo abaixo e envie ao profissional.
                              </div>
                            ) : (
                              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed text-center mb-6">
                                Um e-mail de ativação foi enviado para <strong className="text-indigo-600 dark:text-indigo-400">{invitedProfile.email}</strong>.
                                <br />
                                Por motivos de segurança, o novo perfil permanecerá com status <strong>"Aguardando Cadastro"</strong> e não poderá ser acessado por este mesmo login.
                              </p>
                            )}

                            <div className="bg-slate-50 dark:bg-slate-950/40 border border-slate-200/50 dark:border-slate-800 rounded-2xl p-4 mb-6">
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Link exclusivo de cadastro</p>
                              <div className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800">
                                <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate flex-1">
                                  {invitedProfile.inviteUrl}
                                </span>
                                <button 
                                  onClick={() => handleCopyLink(invitedProfile.inviteUrl || `${window.location.origin}/`)}
                                  className="p-2 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-lg transition-all"
                                  title="Copiar Link"
                                >
                                  {isCopied ? <span className="text-[9px] font-black uppercase text-emerald-600">Copiado!</span> : <Clipboard size={12} />}
                                </button>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <button
                                onClick={() => handleSimulateRegistration(invitedProfile)}
                                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md shadow-indigo-600/10 text-center flex items-center justify-center gap-2 cursor-pointer"
                              >
                                Simular Cadastro do Profissional
                              </button>
                              <button
                                onClick={() => setInvitedProfile(null)}
                                className="w-full py-3 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all text-center cursor-pointer"
                              >
                                Concluir e Fechar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
               </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="max-w-4xl mx-auto pb-20 space-y-8 animate-in fade-in duration-500">
               <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden transition-colors">
                <div className="bg-indigo-900 p-5 sm:p-10 text-white flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="bg-white/10 p-4 rounded-3xl"><SettingsIcon size={28} /></div>
                    <div>
                      <h3 className="text-3xl font-black tracking-tight">Configurações do Consultório</h3>
                      <p className="text-indigo-300 text-[10px] font-black uppercase tracking-[0.2em] mt-1.5">Gerencie seu perfil e preferências</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 sm:p-12 space-y-10 sm:space-y-16">
                   {/* Ajuda e Tour */}
                   <section className="space-y-8">
                    <div className="flex items-center gap-3">
                      <HelpCircle className="text-indigo-500" size={20} />
                      <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Ajuda e Suporte</h4>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-5 sm:p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-700 flex flex-col md:flex-row items-center justify-between gap-6">
                      <div className="flex items-center gap-5">
                        <div className="p-4 bg-indigo-600 text-white rounded-2xl shadow-lg"><PlayCircle size={28} /></div>
                        <div>
                          <p className="text-sm font-black text-slate-800 dark:text-white uppercase tracking-tight">Tour Guiado Allodos</p>
                          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Aprenda a navegar pelas principais funções</p>
                        </div>
                      </div>
                      <button 
                        onClick={handleRestartWizard}
                        className="w-full md:w-auto px-5 sm:px-8 py-3.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-indigo-500 transition-all dark:text-white"
                      >
                        Reiniciar Tour
                      </button>
                    </div>
                   </section>

                   {/* Tema */}
                   <section className="space-y-8">
                    <div className="flex items-center gap-3">
                      <Palette className="text-indigo-500" size={20} />
                      <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Interface Visual</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <button onClick={() => setIsDarkMode(false)} className={`flex items-center gap-5 p-5 sm:p-8 rounded-[2.5rem] border-2 transition-all ${!isDarkMode ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/20' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'}`}>
                        <div className={`p-4 rounded-2xl ${!isDarkMode ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}><Sun size={28} /></div>
                        <span className="font-black uppercase tracking-widest text-xs">Modo Claro</span>
                      </button>
                      <button onClick={() => setIsDarkMode(true)} className={`flex items-center gap-5 p-5 sm:p-8 rounded-[2.5rem] border-2 transition-all ${isDarkMode ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/20' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'}`}>
                        <div className={`p-4 rounded-2xl ${isDarkMode ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}><Moon size={28} /></div>
                        <span className="font-black uppercase tracking-widest text-xs">Modo Escuro</span>
                      </button>
                    </div>
                   </section>

                   {/* Identidade e Perfil */}
                   <form onSubmit={handleSaveSettings} className="space-y-8 sm:space-y-12">
                     <section className="space-y-10">
                        <div className="flex items-center gap-3">
                          <Building className="text-indigo-500" size={20} />
                          <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Identidade Clínica</h4>
                          {!canEditClinic && (
                            <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-lg">
                              Somente leitura · definida pelo administrador
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-col md:flex-row gap-6 sm:gap-12 items-center md:items-start">
                          <div className="relative group">
                            <div className="w-40 h-40 bg-slate-100 dark:bg-slate-800 rounded-[2rem] sm:rounded-[3rem] flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300 dark:border-slate-700 group-hover:border-indigo-500 transition-all">
                               {therapist.logo ? (
                                 <img src={therapist.logo} alt="Logo" className="w-full h-full object-cover" />
                               ) : (
                                 <ImageIcon size={40} className="text-slate-300 dark:text-slate-600" />
                               )}
                            </div>
                            {canEditClinic && (
                              <label className="absolute -bottom-3 -right-3 p-4 bg-indigo-600 text-white rounded-3xl shadow-2xl hover:scale-110 active:scale-95 transition-all cursor-pointer border-4 border-white dark:border-slate-900">
                                 <Camera size={22} />
                                 <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                              </label>
                            )}
                          </div>

                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Nome da Clínica / Consultório</label>
                              <div className="relative">
                                <Building className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input value={therapist.clinicName} readOnly={!canEditClinic} disabled={!canEditClinic} onChange={(e) => setTherapist(prev => ({ ...prev, clinicName: e.target.value }))} className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">CNPJ</label>
                              <div className="relative">
                                <FileText className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input value={therapist.cnpj || ''} readOnly={!canEditClinic} disabled={!canEditClinic} onChange={(e) => setTherapist(prev => ({ ...prev, cnpj: e.target.value }))} placeholder="00.000.000/0000-00" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                            </div>
                          </div>
                        </div>
                     </section>

                     <section className="space-y-10">
                        <div className="flex items-center gap-3">
                          <Phone className="text-indigo-500" size={20} />
                          <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Contato e Localização</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Telefone</label>
                              <div className="relative">
                                <Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input value={therapist.phone} readOnly={!canEditClinic} disabled={!canEditClinic} onChange={(e) => setTherapist(prev => ({ ...prev, phone: e.target.value }))} placeholder="(00) 0000-0000" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                           </div>
                           <div className="space-y-2">
                              <label htmlFor="clinic-whatsapp" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">WhatsApp</label>
                              <div className="relative">
                                <MessageCircle className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input id="clinic-whatsapp" inputMode="tel" value={therapist.clinicWhatsapp || ''} readOnly={!canEditContactWhatsappField} disabled={!canEditContactWhatsappField} onChange={(e) => setTherapist(prev => ({ ...prev, clinicWhatsapp: e.target.value }))} placeholder="(00) 0 0000-0000" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                              <p className="text-[10px] font-semibold text-slate-400 ml-1">
                                {activeProfile?.role === 'Recepcionista' ? 'Seu número: recebe os avisos de novos agendamentos da recepção.' : 'Recebe os avisos automáticos destinados à recepção.'}
                              </p>
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">E-mail Profissional</label>
                              <div className="relative">
                                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input value={therapist.email} readOnly={!canEditClinic} disabled={!canEditClinic} onChange={(e) => setTherapist(prev => ({ ...prev, email: e.target.value }))} placeholder="email@consultorio.com" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Site</label>
                              <div className="relative">
                                <Globe className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input value={therapist.website || ''} readOnly={!canEditClinic} disabled={!canEditClinic} onChange={(e) => setTherapist(prev => ({ ...prev, website: e.target.value }))} placeholder="www.seusite.com.br" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                           </div>
                           <div className="space-y-2 md:col-span-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Endereço do Consultório</label>
                              <div className="relative">
                                <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input value={therapist.address || ''} readOnly={!canEditClinic} disabled={!canEditClinic} onChange={(e) => setTherapist(prev => ({ ...prev, address: e.target.value }))} placeholder="Av. Paulista, 1000 - São Paulo, SP" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                              </div>
                           </div>
                        </div>
                     </section>

                      <section className="space-y-10">
                         <div className="flex items-center gap-3">
                           <UserIcon className="text-indigo-500" size={20} />
                           <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Identidade do Profissional</h4>
                         </div>
                         
                         <div className="flex flex-col md:flex-row gap-6 sm:gap-12 items-center md:items-start">
                           <div className="relative group">
                             <div className="w-40 h-40 bg-slate-100 dark:bg-slate-800 rounded-[2rem] sm:rounded-[3rem] flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300 dark:border-slate-700 group-hover:border-indigo-500 transition-all">
                                {therapist.professionalPhoto ? (
                                  <img src={therapist.professionalPhoto} alt="Foto" className="w-full h-full object-cover" />
                                ) : (
                                  <UserIcon size={40} className="text-slate-300 dark:text-slate-600" />
                                )}
                             </div>
                             <label className="absolute -bottom-3 -right-3 p-4 bg-indigo-600 text-white rounded-3xl shadow-2xl hover:scale-110 active:scale-95 transition-all cursor-pointer border-4 border-white dark:border-slate-900">
                                <Camera size={22} />
                                <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                             </label>
                           </div>

                           <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                             <div className="space-y-2">
                               <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Nome Profissional</label>
                               <input value={therapist.name} onChange={(e) => setTherapist(prev => ({ ...prev, name: e.target.value }))} className="w-full p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm" />
                             </div>
                             <div className="space-y-2">
                               <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">CRP</label>
                               <input value={therapist.crp} onChange={(e) => setTherapist(prev => ({ ...prev, crp: e.target.value }))} className="w-full p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm" />
                             </div>
                             <div className="space-y-2">
                               <label htmlFor="professional-whatsapp" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">WhatsApp</label>
                               <div className="relative">
                                 <MessageCircle className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                 <input id="professional-whatsapp" inputMode="tel" value={therapist.whatsapp || ''} onChange={(e) => setTherapist(prev => ({ ...prev, whatsapp: e.target.value }))} placeholder="(00) 0 0000-0000" className="w-full p-5 pl-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl text-sm font-bold dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" />
                               </div>
                               <p className="text-[10px] font-semibold text-slate-400 ml-1">Recebe os seus avisos automáticos (ex.: novo agendamento).</p>
                             </div>
                           </div>
                         </div>
                      </section>

                     <div className="pt-10 relative">
                       {showSettingsToast && (
                         <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-5 sm:px-8 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2 animate-in slide-in-from-bottom-2 shadow-xl">
                           <CheckCircle size={16} /> Alterações Salvas com Sucesso
                         </div>
                       )}
                       <button type="submit" className="w-full p-6 bg-indigo-600 text-white rounded-[2.5rem] font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-indigo-600/20 dark:shadow-none hover:bg-indigo-700 active:scale-95 transition-all">
                         Atualizar Configurações do Consultório
                       </button>
                     </div>
                   </form>

                   {/* Exportação de Prontuário e Cadastro */}
                   <section className="pt-12 border-t dark:border-slate-800 space-y-8">
                     <div className="flex items-center gap-3">
                       <Download className="text-indigo-500" size={20} />
                       <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Exportar Prontuário e Cadastro</h4>
                     </div>
                     <div className="bg-slate-50 dark:bg-slate-800/40 p-5 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-slate-100 dark:border-slate-800 space-y-6">
                       <div>
                         <p className="text-lg font-black text-slate-800 dark:text-white tracking-tight">Fazer Download dos Dados do Paciente</p>
                         <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest mt-1 leading-relaxed">
                           Selecione um paciente abaixo para exportar todas as informações de cadastro e prontuário em formato PDF ou CSV.
                         </p>
                       </div>

                       <div className="flex flex-col sm:flex-row gap-4 items-center">
                         <div className="w-full sm:flex-1 relative">
                           <select 
                             value={exportPatientId || ''} 
                             onChange={(e) => setExportPatientId(e.target.value)}
                             className="w-full p-4 pr-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs font-bold dark:text-white outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 appearance-none shadow-sm cursor-pointer"
                           >
                             <option value="">Selecione um paciente...</option>
                             {/* Bloco 27: só os pacientes visíveis ao perfil logado
                                 (profissional vê apenas os seus; admin, a clínica). */}
                             {filteredPatients.map(p => (
                               <option key={p.id} value={p.id}>{p.name}</option>
                             ))}
                           </select>
                           <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                             <ChevronDown size={16} />
                           </div>
                         </div>

                         <div className="flex gap-3 w-full sm:w-auto">
                           <button
                             type="button"
                             onClick={handleDownloadPDF}
                             disabled={!exportPatientId}
                             className={`flex-1 sm:flex-none px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${exportPatientId ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/10 active:scale-95 cursor-pointer' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'}`}
                           >
                             <FileText size={14} /> Download PDF
                           </button>
                           <button
                             type="button"
                             onClick={handleDownloadCSV}
                             disabled={!exportPatientId}
                             className={`flex-1 sm:flex-none px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${exportPatientId ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/10 active:scale-95 cursor-pointer' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'}`}
                           >
                             <Clipboard size={14} /> Download CSV
                           </button>
                         </div>
                       </div>
                     </div>
                   </section>

                    {/* Bloco 63: importação de pacientes por CSV (Profissional importa só para si) */}
                    <ImportData
                      patients={patients}
                      professionals={clinicProfessionals}
                      activeProfile={activeProfile}
                      onImported={() => refetchPatients()}
                    />

                    {/* Bloco 37: backup automático por e-mail (todos os perfis, conteúdo conforme o privilégio) */}
                    <BackupSettings />

                    {/* Zona de Perigo: exclusiva do administrador */}
                    {canEditClinic && (
                    <section className="pt-12 border-t dark:border-slate-800 space-y-8">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="text-rose-500" size={20} />
                        <h4 className="text-[11px] font-black text-rose-500 uppercase tracking-[0.3em]">Zona de Perigo</h4>
                      </div>
                      <div className="bg-rose-50 dark:bg-rose-950/20 p-5 sm:p-10 rounded-[2rem] sm:rounded-[3rem] border border-rose-100 dark:border-rose-900/40 space-y-6">
                        <div>
                          <p className="text-lg font-black text-rose-900 dark:text-rose-100 tracking-tight">Apagar Todos os Dados</p>
                          <p className="text-xs text-rose-600 dark:text-rose-400 font-bold uppercase tracking-widest mt-1 leading-relaxed">
                            Esta ação irá remover permanentemente todos os prontuários, ciclos clínicos, transcrições e áudios. Esta operação não pode ser desfeita.
                          </p>
                        </div>
                        <button 
                          onClick={() => setIsDeleteModalOpen(true)}
                          className="px-5 sm:px-10 py-4 bg-rose-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-700 transition-all shadow-xl shadow-rose-600/20 active:scale-95 flex items-center gap-2"
                        >
                          <Trash2 size={16} /> Deletar Todos os Dados do Sistema
                        </button>
                      </div>
                    </section>
                    )}
                </div>
               </div>
            </div>
          )}
        </div>
      </main>

      {isNewPatientModalOpen && (
        <NewPatientModal
          onClose={() => { setIsNewPatientModalOpen(false); setNewPatientError(null); }}
          onSave={handleAddPatient}
          userProfiles={userProfiles}
          activeProfile={activeProfile}
          errorMessage={newPatientError}
          isSaving={isSavingPatient}
        />
      )}

      {/* Modal de Confirmação de Deleção */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] w-full max-w-md p-5 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95">
            <div className="w-20 h-20 bg-rose-100 dark:bg-rose-900/30 rounded-3xl flex items-center justify-center text-rose-600 dark:text-rose-400 mx-auto mb-8">
              <AlertTriangle size={40} />
            </div>
            <h3 className="text-2xl font-black text-slate-900 dark:text-white text-center tracking-tight mb-4">Confirmar Exclusão Total?</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center leading-relaxed mb-10 font-medium">
              Você está prestes a apagar permanentemente <span className="font-black text-rose-600">TODOS</span> os dados do seu consultório. Esta ação é irreversível e afetará prontuários, ciclos e áudios.
            </p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={handleDeleteAllData}
                className="w-full p-5 bg-rose-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-rose-700 transition-all shadow-lg shadow-rose-600/20"
              >
                Sim, Apagar Tudo Agora
              </button>
              <button 
                onClick={() => setIsDeleteModalOpen(false)}
                className="w-full p-5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
              >
                Cancelar e Manter Dados
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
